import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { withRouter } from 'react-router-dom';
import { useApolloClient } from 'react-apollo';
import gql from 'graphql-tag';
import * as Cookies from 'es-cookie';
import moment from 'moment';
import DateBlock from '../home_bck/DateBlock';
import './_assignment.scss';
import {
    useModuleGuard,
    AccessDeniedModal,
    AccessDeniedCard,
} from '../../../utils/moduleGuard';
import {
    getAuthorizedClassIds,
    getAuthorizedSectionIds,
    getAuthorizedSubjectIds,
    filterByAuthorizedIds,
} from '../../utils/authHelper';
import { scopeBranches, onBranchScopeChange } from '../../../utils/branchScope';

// /assignment (and /assignment/dashboard, /create, /submissions) → "Assignments"
// (see MODULE_ROUTES in moduleAccessGuard).
// module_key ONLY, never module_name: the sidebar group name is shared by sibling
// modules, and can() grants an action when ANY matched row allows it — matching the
// group would let another module's grant leak in.
//
// Module-level constant on purpose: useModuleGuard memoises on this array's identity,
// so an inline literal would rebuild the guard on every render.
const ASSIGNMENTS_MODULE = ['Assignments'];

// ─── Sample Data ──────────────────────────────────────────────────────────────
const INIT_STUDENTS = [
    { id: 1,  name: 'Arjun Sharma',   roll: '12A01', email: 'arjun@school.in'   },
    { id: 2,  name: 'Priya Patel',    roll: '12A02', email: 'priya@school.in'   },
    { id: 3,  name: 'Rohan Mehta',    roll: '12A03', email: 'rohan@school.in'   },
    { id: 4,  name: 'Ananya Singh',   roll: '12A04', email: 'ananya@school.in'  },
    { id: 5,  name: 'Kabir Khanna',   roll: '12A05', email: 'kabir@school.in'   },
    { id: 6,  name: 'Divya Reddy',    roll: '12B01', email: 'divya@school.in'   },
    { id: 7,  name: 'Vivek Joshi',    roll: '12B02', email: 'vivek@school.in'   },
    { id: 8,  name: 'Sneha Iyer',     roll: '12B03', email: 'sneha@school.in'   },
    { id: 9,  name: 'Karan Malhotra', roll: '11A01', email: 'karan@school.in'   },
    { id: 10, name: 'Nisha Gupta',    roll: '11A02', email: 'nisha@school.in'   },
];

const INIT_GROUPS = [
    { id: 1, name: 'Class 12 – Section A', studentIds: [1, 2, 3, 4, 5] },
    { id: 2, name: 'Class 12 – Section B', studentIds: [6, 7, 8]        },
    { id: 3, name: 'Class 11 – Section A', studentIds: [9, 10]          },
];

const INIT_ASSIGNMENTS = [
    { id: 1, title: "Newton's Laws – Problem Set",   subject: 'Physics',     chapter: 'Laws of Motion',       description: "Solve 20 numericals on Newton's three laws. Include free-body diagrams.",    instructions: 'Show all steps. Use SI units. Submit as PDF.',                         maxMarks: 50,  startDate: '2026-05-28', dueDate: '2026-06-05', status: 'active', groupIds: [1]       },
    { id: 2, title: 'Essay: Industrial Revolution',  subject: 'History',     chapter: 'Industrial Revolution', description: 'Write a 1500-word essay on social and economic impacts of the revolution.', instructions: 'Include 5 references. Times New Roman 12pt, double-spaced.',           maxMarks: 100, startDate: '2026-05-25', dueDate: '2026-06-03', status: 'active', groupIds: [1, 2]    },
    { id: 3, title: 'Integration Practice Problems', subject: 'Mathematics', chapter: 'Integral Calculus',    description: 'Solve 15 problems on definite and indefinite integrals.',                    instructions: 'Show every step. Box final answers.',                                  maxMarks: 50,  startDate: '2026-06-02', dueDate: '2026-06-12', status: 'active', groupIds: [1]       },
    { id: 4, title: 'Cell Division Diagrams',        subject: 'Biology',     chapter: 'Cell Biology',         description: 'Draw and label all stages of mitosis with clear diagrams.',                 instructions: 'Use coloured pencils. Scan and upload as PDF.',                        maxMarks: 40,  startDate: '2026-06-01', dueDate: '2026-06-15', status: 'active', groupIds: [2, 3]    },
    { id: 5, title: 'Organic Reactions Worksheet',   subject: 'Chemistry',   chapter: 'Organic Chemistry',    description: 'Complete the worksheet on substitution, addition, and elimination reactions.', instructions: 'Draw structural formulas clearly. Show curved-arrow mechanisms.',       maxMarks: 30,  startDate: '2026-06-05', dueDate: '2026-06-20', status: 'draft',  groupIds: [1, 2, 3] },
];

const INIT_SUBMISSIONS = [
    { id: 1, assignmentId: 1, studentId: 1,  answerText: "Q1: F=ma. Mass=5kg, a=3m/s², F=15N.\nQ2: Newton's 1st law — an object at rest stays at rest unless acted on by a net force.\nQ3: Action-reaction — when I push a wall, it pushes me back equally.", submittedAt: '2026-06-01T09:30', status: 'graded',    grade: 45,   feedback: 'Excellent! Very detailed solutions. All diagrams correct.',     updatedAt: '2026-06-02T10:00' },
    { id: 2, assignmentId: 1, studentId: 2,  answerText: "Solutions for Newton's Laws:\nQ1–Q5 completed with diagrams.\nQ6: Net force = 20N, Acceleration = 4 m/s².",                                                                                              submittedAt: '2026-06-02T14:15', status: 'graded',    grade: 38,   feedback: 'Good work but missed diagrams for Q4 and Q7.',                 updatedAt: '2026-06-02T16:00' },
    { id: 3, assignmentId: 1, studentId: 3,  answerText: "My solutions for the assignment. All 20 questions attempted.",                                                                                                                                             submittedAt: '2026-06-03T11:00', status: 'submitted', grade: null, feedback: '',                                                            updatedAt: '2026-06-03T11:00' },
    { id: 4, assignmentId: 1, studentId: 7,  answerText: "Physics assignment — solved all numericals. See working below for each question.",                                                                                                                         submittedAt: '2026-05-31T16:30', status: 'graded',    grade: 42,   feedback: 'Well organised. Minor unit errors in Q3.',                     updatedAt: '2026-06-01T09:00' },
    { id: 5, assignmentId: 2, studentId: 1,  answerText: "The Industrial Revolution began in Britain in the late 18th century, transforming manufacturing from cottage industries to factory systems. Key impacts include urbanisation, child labour reforms, and rise of the middle class.", submittedAt: '2026-06-01T20:00', status: 'submitted', grade: null, feedback: '', updatedAt: '2026-06-01T20:00' },
    { id: 6, assignmentId: 2, studentId: 6,  answerText: "Industrial Revolution Essay — examining economic and social transformation across Britain and Europe.",                                                                                                     submittedAt: '2026-06-02T08:45', status: 'graded',    grade: 88,   feedback: 'Excellent essay. Well researched and structured.',             updatedAt: '2026-06-02T12:00' },
    { id: 7, assignmentId: 3, studentId: 2,  answerText: "Q1: ∫(2x+3)dx = x²+3x+C\nQ2: ∫sin(x)dx = −cos(x)+C\nQ3: ∫eˣdx = eˣ+C",                                                                                                                               submittedAt: '2026-06-05T10:00', status: 'submitted', grade: null, feedback: '',                                                            updatedAt: '2026-06-05T10:00' },
    { id: 8, assignmentId: 4, studentId: 9,  answerText: "Mitosis stages:\n1. Prophase — chromosomes condense, spindle forms.\n2. Metaphase — chromosomes align at equator.\n3. Anaphase — sister chromatids separate.\n4. Telophase — nuclear envelope reforms.", submittedAt: '2026-06-08T14:00', status: 'submitted', grade: null, feedback: '',                                                            updatedAt: '2026-06-08T14:00' },
];

// ─── Assignment Mutation ──────────────────────────────────────────────────────
const ADD_ASSIGNMENT = gql`
    mutation addStudentAssignment(
        $assignment_id: Int, $title: String, $description: String, $mediafiles_mapping_id: Int,
        $file_path: String, $dairy_assignment_type_id: Int,
        $from_date: String, $to_date: String, $assignment_marks: Int,
        $employee_id: Int, $institute_id: Int, $branch_id: Int,
        $class_id: String, $subject_id: Int, $section_ids: String, $estatus: Int
    ) {
        addStudentAssignment(params: {
            assignment_id: $assignment_id, title: $title, description: $description,
            mediafiles_mapping_id: $mediafiles_mapping_id,
            file_path: $file_path, dairy_assignment_type_id: $dairy_assignment_type_id,
            from_date: $from_date, to_date: $to_date,
            assignment_marks: $assignment_marks, employee_id: $employee_id,
            institute_id: $institute_id, branch_id: $branch_id,
            class_id: $class_id, subject_id: $subject_id,
            section_ids: $section_ids, estatus: $estatus
        })
    }
`;

// ─── Update Assignment Mutation ───────────────────────────────────────────────
const UPDATE_ASSIGNMENT = gql`
    mutation updateStudentAssignment(
        $assignment_id: Int!, $title: String, $description: String,
        $file_path: String, $from_date: String, $to_date: String,
        $assignment_marks: Int, $employee_id: Int, $branch_id: Int,
        $class_id: String, $subject_id: Int, $section_ids: String, $estatus: Int
    ) {
        updateStudentAssignment(params: {
            assignment_id: $assignment_id, title: $title, description: $description,
            file_path: $file_path, from_date: $from_date, to_date: $to_date,
            assignment_marks: $assignment_marks, employee_id: $employee_id,
            branch_id: $branch_id, class_id: $class_id,
            subject_id: $subject_id, section_ids: $section_ids, estatus: $estatus
        })
    }
`;

// ─── Grading: fetch submissions for one assignment ────────────────────────────
const GET_ASSIGNMENT_SUBMISSIONS = gql`
    query getAssignmentSubmissions($assignment_id: Int) {
        getAssignmentSubmissions(assignment_id: $assignment_id) {
            id
            dairy_assignment_id
            edu_student_id
            status
            upload_file_path
            marks
            comments
            submitted_time
            verified_by
            verified_time
            verifier_comments
            timestamp
            student_name
            student_roll_no
        }
    }
`;

const GET_ALL_SUBMISSIONS = gql`
    query getAllAssignmentSubmissions {
        getAssignmentSubmissions {
            id
            dairy_assignment_id
            edu_student_id
            status
            marks
            submitted_time
            verified_time
            timestamp
            student_name
            student_roll_no
        }
    }
`;

const UPDATE_ASSIGNMENT_SUBMISSION = gql`
    mutation updateAssignmentSubmission(
        $id: Int!, $marks: Int, $verifier_comments: String, $verified_by: Int
    ) {
        updateAssignmentSubmission(params: {
            id: $id
            marks: $marks
            verifier_comments: $verifier_comments
            verified_by: $verified_by
        })
    }
`;

// ─── Delete Assignment Mutation ───────────────────────────────────────────────
const DELETE_ASSIGNMENT = gql`
    mutation deleteStudentAssignment($assignment_id: ID!) {
        deleteStudentAssignment(assignment_id: $assignment_id)
    }
`;

// ─── File Upload Mutation ─────────────────────────────────────────────────────
const FILE_UPLOAD = gql`
    mutation fileUpload($category: String!, $file: [FileBase64Input!]!, $client_id: Int!) {
        fileUpload(params: { category: $category, file: $file, client_id: $client_id }) {
            file_url
            file_name
            file_extension_type
        }
    }
`;

// ─── Fetch Assignments Query ──────────────────────────────────────────────────
const GET_STUDENT_ASSIGNMENTS = gql`
    query getStudentAssignmentDetails(
        $institute_id: Int
        $branch_id: String
        $class_id: String
        $section_ids: String
        $dairy_assignment_type_id: Int
        $from_date: String
        $to_date: String
    ) {
        getStudentAssignmentDetails(
            institute_id: $institute_id
            branch_id: $branch_id
            class_id: $class_id
            section_ids: $section_ids
            dairy_assignment_type_id: $dairy_assignment_type_id
            from_date: $from_date
            to_date: $to_date
        ) {
            id
            title
            description
            file_path
            dairy_assignment_type_id
            from_date
            to_date
            assignment_marks
            employee_id
            institute_id
            branch_id
            class_id
            subject_id
            inserted_by
            inserted_time
            estatus
            section_ids
            student_count
            subject_name
            branch_name
            Section_Name
            Class
        }
    }
`;

// ─── Academic Years / Branches / Classes / Sections API ──────────────────────
const GET_ACADEMIC_YEARS = gql`
    query getAcademicYears($id: Int) {
        getAcademicYears(id: $id) {
            id
            academic_year
        }
    }
`;

const GET_BRANCHES = gql`
    query getBranches($institution_id: Int!) {
        getBranches(institution_id: $institution_id) {
            id
            branch_name
            sections {
                id
                class_id
                section_name
                stream_id
                subjects {
                    subject_id
                    subject_name
                }
            }
        }
    }
`;

const GET_CLASSES = gql`
    query($id: Int) {
        getExamClasses(id: $id) {
            id
            class
        }
    }
`;

const GET_STREAMS = gql`
    query {
        getStreams {
            id
            stream
        }
    }
`;

// ─── Stream helpers ────────────────────────────────────────────────────────────
// Stream sits between Class and Section, and applies ONLY to Intermediate classes.
const STREAM_CLASS_IDS = [15, 16];
const isStreamClass = (classId, classList = []) => {
    if (!classId) return false;
    const cid = parseInt(classId);
    if (STREAM_CLASS_IDS.includes(cid)) return true;
    if (Array.isArray(classList) && classList.length) {
        const found = classList.find(c => Number(c.id) === cid);
        if (found && found.class) {
            const name = String(found.class).toLowerCase();
            if (name.includes('11') || name.includes('12') || name.includes('xi') || name.includes('xii') || name.includes('inter')) {
                return true;
            }
        }
    }
    return false;
};

// ─── Faculty API ─────────────────────────────────────────────────────────────
const GET_EMPLOYEE_DETAILS = gql`
    query getEmployeeDetails($institute_id: Int!, $branch_id: Int!) {
        getEmployeeDetails(institute_id: $institute_id, branch_id: $branch_id) {
            employeeId
            employee_name
            subject_ids
        }
    }
`;

const GET_CLASS_SUBJECTS = gql`
    query getClassSubjectsByAcademicYear($institute_id: Int!, $classIds: String, $academic_year_id: Int!) {
        getClassSubjectsByAcademicYear(institute_id: $institute_id, classIds: $classIds, academic_year_id: $academic_year_id) {
            subject_ids
            subject_names
            checked
        }
    }
`;

// ─── Constants ─────────────────────────────────────────────────────────────────
const PURPLE   = '#6366f1';
const COLORS   = ['#6366f1','#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#ec4899'];

let nextId = 100;
const genId = () => ++nextId;

function colorOf(str) { return COLORS[Math.abs([...str].reduce((h,c)=>(h<<5)-h+c.charCodeAt(0),0)) % COLORS.length]; }
function daysLeft(d)  { if (!d) return null; const ms = new Date(d) - new Date(); return isNaN(ms) ? null : Math.ceil(ms / 864e5); }
function fmtDate(d)   { return d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—'; }
function fmtDT(d)     { return d ? new Date(d).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}) : '—'; }

function unixToDate(ts) {
    if (!ts) return '';
    const n = parseInt(ts);
    // Large integer → Unix timestamp in seconds (post-2001: > 978307200)
    if (!isNaN(n) && n > 978307200) {
        return new Date(n * 1000).toISOString().split('T')[0];
    }
    // D/M/YYYY or DD/MM/YYYY string (e.g. "15/6/2026" returned after a mutation)
    const slash = String(ts).split('/');
    if (slash.length === 3) {
        const d = new Date(parseInt(slash[2]), parseInt(slash[1]) - 1, parseInt(slash[0]));
        if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
    }
    // YYYY-MM-DD or any format Date can parse
    const parsed = new Date(ts);
    if (!isNaN(parsed.getTime())) return parsed.toISOString().split('T')[0];
    return '';
}

function mapApiAssignment(a) {
    return {
        id:            a.id,
        title:         a.title || '',
        subject:       a.subject_name || 'Others',
        description:   a.description || '',
        file_path:     a.file_path || '',
        maxMarks:      a.assignment_marks || 0,
        startDate:     unixToDate(a.from_date),
        dueDate:       unixToDate(a.to_date),
        created:       unixToDate(a.inserted_time),
        status:        a.estatus === 1 ? 'active' : 'draft',
        groupIds:      (a.section_ids || '').split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n)),
        student_count: a.student_count || 0,
        branch_name:   a.branch_name || '',
        Section_Name:  a.Section_Name || '',
        class_name:    a.Class || '',
        class_id:      a.class_id  ? String(a.class_id)  : '',
        branch_id:     a.branch_id  ? Number(a.branch_id)  : null,
        subject_id:    a.subject_id ? Number(a.subject_id) : null,
        employee_id:   a.employee_id ? Number(a.employee_id) : null,
    };
}

// ─── Shimmer loading (reused across the assignment module) ─────────────────────
// Inject the keyframes once for the whole module.
function ShimmerStyles() {
    return (
        <style>{`
            @keyframes asgn-shimmer { 0% { background-position: -800px 0; } 100% { background-position: 800px 0; } }
            .asgn-shimmer {
                background: linear-gradient(90deg, #eef0f4 25%, #e2e6ee 50%, #eef0f4 75%);
                background-size: 800px 100%;
                animation: asgn-shimmer 1.4s infinite linear;
                border-radius: 6px;
            }
        `}</style>
    );
}
// A single shimmer bar
function Shimmer({ w = '100%', h = 12, style = {} }) {
    return <div className="asgn-shimmer" style={{ width: w, height: h, ...style }} />;
}
// Shimmer rows for a table body — `widths` defines columns; `rows` how many lines.
function ShimmerTable({ widths, rows = 6 }) {
    return (
        <div style={{ padding: '4px 0' }}>
            <ShimmerStyles />
            <div style={{ display: 'flex', gap: 14, padding: '12px 16px', borderBottom: '2px solid #f3f4f6' }}>
                {widths.map((w, i) => <Shimmer key={i} w={w} h={13} style={{ flexShrink: 0 }} />)}
            </div>
            {Array.from({ length: rows }).map((_, r) => (
                <div key={r} style={{ display: 'flex', gap: 14, padding: '14px 16px', borderBottom: '1px solid #f3f4f6', alignItems: 'center' }}>
                    {widths.map((w, i) => <Shimmer key={i} w={w} h={12} style={{ flexShrink: 0 }} />)}
                </div>
            ))}
        </div>
    );
}

// ─── Shared UI ────────────────────────────────────────────────────────────────
function Badge({ status }) {
    const map = {
        active:    ['#dcfce7','#16a34a','#86efac','Active'],
        draft:     ['#f1f5f9','#64748b','#cbd5e1','Draft'],
        overdue:   ['#fee2e2','#dc2626','#fca5a5','Overdue'],
        submitted: ['#dbeafe','#1d4ed8','#93c5fd','Submitted'],
        graded:    ['#fef3c7','#d97706','#fde68a','Graded'],
        pending:   ['#fff7ed','#ea580c','#fed7aa','Pending'],
        late:      ['#fee2e2','#dc2626','#fca5a5','Late'],
    };
    const [bg,fg,br,label] = map[status] || map.pending;
    return <span style={{background:bg,color:fg,border:`1.5px solid ${br}`,borderRadius:20,padding:'3px 12px',fontSize:11,fontWeight:700,whiteSpace:'nowrap'}}>{label}</span>;
}

function SubjectChip({ subject }) {
    const c = colorOf(subject || 'x');
    return <span style={{background:c+'18',color:c,border:`1.5px solid ${c}33`,borderRadius:20,padding:'3px 11px',fontSize:11,fontWeight:700}}>{subject}</span>;
}

function StatCard({ icon, label, value, color, bg, loading }) {
    return (
        <div style={{background:'#fff',borderRadius:16,padding:'18px 20px',display:'flex',alignItems:'center',gap:16,boxShadow:'0 4px 16px rgba(15,23,42,0.07)',border:'1px solid #f1f5f9',borderTop:`4px solid ${color}`,marginBottom:16}}>
            <div style={{width:50,height:50,background:bg,borderRadius:14,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <i className={`fas ${icon}`} style={{color,fontSize:20}} />
            </div>
            <div>
                <div style={{fontSize:11,fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px'}}>{label}</div>
                {loading
                    ? <div style={{marginTop:6}}><Shimmer w={42} h={24} /></div>
                    : <div style={{fontSize:28,fontWeight:800,color,lineHeight:1.2}}>{value}</div>}
            </div>
        </div>
    );
}

function SecHead({ icon, title, action }) {
    return (
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16,paddingBottom:10,borderBottom:'1px solid #f1f5f9'}}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
                <div style={{width:30,height:30,background:`${PURPLE}15`,borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <i className={`fas ${icon}`} style={{color:PURPLE,fontSize:13}} />
                </div>
                <span style={{fontWeight:800,fontSize:15,color:'#1f2937'}}>{title}</span>
            </div>
            {action}
        </div>
    );
}

function PrimaryBtn({ children, onClick, style={}, disabled=false }) {
    return (
        <button onClick={onClick} disabled={disabled}
            style={{background:`linear-gradient(135deg,${PURPLE},#8b5cf6)`,color:'#fff',border:'none',borderRadius:10,padding:'10px 22px',fontSize:13,fontWeight:700,cursor:disabled?'not-allowed':'pointer',opacity:disabled?.6:1,display:'inline-flex',alignItems:'center',gap:7,...style}}>
            {children}
        </button>
    );
}

function GhostBtn({ children, onClick, danger=false, style={} }) {
    return (
        <button onClick={onClick}
            style={{background:danger?'#fee2e2':'#f1f5f9',color:danger?'#dc2626':'#374151',border:danger?'1.5px solid #fca5a5':'1.5px solid #e2e8f0',borderRadius:10,padding:'8px 16px',fontSize:12,fontWeight:700,cursor:'pointer',...style}}>
            {children}
        </button>
    );
}

// ─── CheckboxDropdown (fee-structure style) ───────────────────────────────────
function CheckboxDropdown({ options, value, onChange, placeholder, disabled = false }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const toggle = id => onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);

    const label = value.length === 0
        ? placeholder
        : value.length === options.length
            ? `All (${options.length})`     
            : value.length === 1
                ? (options.find(o => o.value === value[0])?.label || `${value.length} selected`)
                : `${value.length} selected`;

    return (     
        <div ref={ref} style={{ position: 'relative' }}>
            <div onClick={() => !disabled && setOpen(o => !o)}
                style={{ borderRadius: 10, border: '1.5px solid #e5e7eb', padding: '9px 14px', fontSize: 14, color: value.length ? '#1f2937' : '#9ca3af', background: disabled ? '#f3f4f6' : '#f9fafb', cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none', opacity: disabled ? 0.6 : 1 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                <i className={`fal fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0, marginLeft: 6 }} />
            </div>
            {open && (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.10)', zIndex: 9999, maxHeight: 220, overflowY: 'auto' }}>
                    {options.length === 0 ? (
                        <div style={{ padding: '10px 14px', fontSize: 12, color: '#9ca3af' }}>No options available</div>
                    ) : (
                        <>
                            <div onClick={() => onChange(value.length === options.length ? [] : options.map(o => o.value))}
                                style={{ padding: '8px 12px', fontSize: 12, fontWeight: 700, color: '#6366f1', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type="checkbox" readOnly checked={value.length === options.length && options.length > 0} style={{ accentColor: '#6366f1' }} />
                                Select All
                            </div>
                            {options.map(o => (
                                <div key={o.value} onClick={() => toggle(o.value)}
                                    style={{ padding: '7px 12px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, background: value.includes(o.value) ? '#f5f3ff' : 'transparent', borderBottom: '1px solid #f9fafb' }}>
                                    <input type="checkbox" readOnly checked={value.includes(o.value)} style={{ accentColor: '#6366f1' }} />
                                    <span style={{ color: value.includes(o.value) ? '#6d28d9' : '#374151', fontWeight: value.includes(o.value) ? 600 : 400 }}>{o.label}</span>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Assignment Form (create / edit) ─────────────────────────────────────────
// `guard` is the shared useModuleGuard instance from AssignmentHome — passed down so a
// denied action here opens the SAME Access Denied modal rendered by the parent.
function AssignmentForm({ groups, editing, onSave, onCancel, guard }) {
    const client = useApolloClient();
    const blank = { title:'',subject:'',faculty:'',employee_id:'',chapter:'',description:'',instructions:'',maxMarks:'',startDate:'',dueDate:'',status:'active',groupIds:[],fileName:'',fileData:null,filePath:'' };
    const initForm = editing
        ? { ...blank, ...editing, maxMarks: String(editing.maxMarks || ''), groupIds: [...(editing.groupIds || [])], filePath: editing.file_path || '', fileName: editing.file_path ? editing.file_path.split('/').pop() : '' }
        : blank;
    const [form, setForm]   = useState(initForm);
    const [errors,   setErrors]  = useState({});
    const [toast,    setToast]   = useState('');
    const [saving,   setSaving]  = useState(false);
    const [success,  setSuccess] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [academicYears, setAcademicYears] = useState([]);
    const [selYear,       setSelYear]       = useState(null);
    const [branches,      setBranches]      = useState([]);
    const [loadingBr,     setLoadingBr]     = useState(true);
    const [classes,       setClasses]       = useState([]);
    const [faculty,  setFaculty]  = useState([]);

    const [selBranch,    setSelBranch]    = useState(editing?.branch_id || null);
    const [selClasses,   setSelClasses]   = useState(editing?.class_id ? [Number(editing.class_id)] : []);
    const [allStreams,    setAllStreams]   = useState([]);
    const [selStreams,    setSelStreams]   = useState([]);
    const [selSections,  setSelSections]  = useState(editing?.groupIds ? [...editing.groupIds] : []);
    const [classSubjects,   setClassSubjects]   = useState([]);
    const [loadingSubjects, setLoadingSubjects] = useState(false);
    const [loadingFaculty, setLoadingFaculty] = useState(false);

    // Snapshot of editable fields at open time — used to detect no-changes on save
    const initialSnapshot = useRef(JSON.stringify({
        title:       initForm.title,
        subject:     initForm.subject,
        description: initForm.description,
        maxMarks:    initForm.maxMarks,
        startDate:   initForm.startDate,
        dueDate:     initForm.dueDate,
        status:      initForm.status,
        groupIds:    [...(initForm.groupIds || [])].sort((a,b)=>a-b),
        filePath:    initForm.filePath || '',
    }));

    const fetchFaculty = (subjectId) => {
        if (!subjectId || !selBranch) { setFaculty([]); return; }
        const institutionId = parseInt(Cookies.get('institutionid'));
        setLoadingFaculty(true);
        client.query({
            query: GET_EMPLOYEE_DETAILS,
            variables: { institute_id: institutionId, branch_id: 0 },
            fetchPolicy: 'network-only',
        }).then(res => {
            const all = res.data?.getEmployeeDetails || [];
            let filtered = all.filter(emp => {
                const raw = emp.subject_ids;
                const empIds =
                    raw == null             ? [] :
                    typeof raw === 'number' ? [raw] :
                    typeof raw === 'string' ? raw.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n)) :
                    Array.isArray(raw)      ? raw.map(Number) : [];
                return empIds.includes(Number(subjectId));
            });
            const cookieEmpId = Cookies.get('id') || Cookies.get('employee_id');
            if (cookieEmpId) {
                const matched = filtered.filter(emp => String(emp.employeeId || emp.id) === String(cookieEmpId));
                if (matched.length > 0) filtered = matched;
            }
            setFaculty(filtered);
            setLoadingFaculty(false);
        }).catch(() => { setFaculty([]); setLoadingFaculty(false); });
    };

    const fetchClassSubjects = (classId, yearId) => {
        if (!classId) { setClassSubjects([]); return; }
        
        const branchObj = branches.find(b => Number(b.id) === Number(selBranch));
        const branchSecs = branchObj ? (branchObj.sections || []) : [];
        const classSecs = branchSecs.filter(s => Number(s.class_id) === Number(classId));
        const branchSubjs = classSecs.flatMap(s => s.subjects || []);

        let subjects = [];
        const seenIds = new Set();
        branchSubjs.forEach(sub => {
            if (sub && sub.subject_id && !seenIds.has(String(sub.subject_id))) {
                seenIds.add(String(sub.subject_id));
                subjects.push({ subject_ids: String(sub.subject_id), subject_names: sub.subject_name });
            }
        });

        if (subjects.length > 0) {
            const authorizedSubjects = filterByAuthorizedIds(subjects, getAuthorizedSubjectIds(), 'subject_ids');
            setClassSubjects(authorizedSubjects);
            setLoadingSubjects(false);
            return;
        }

        const institutionId = parseInt(Cookies.get('institutionid'));
        setLoadingSubjects(true);
        client.query({
            query: GET_CLASS_SUBJECTS,
            variables: {
                institute_id:     institutionId,
                classIds:         String(classId),
                academic_year_id: yearId ? parseInt(yearId) : null,
            },
            fetchPolicy: 'network-only',
        }).then(res => {
            const raw = res.data?.getClassSubjectsByAcademicYear || [];
            raw.forEach(row => {
                const ids    = (row.subject_ids    || '').split(',').map(s => s.trim());
                const names  = (row.subject_names  || '').split(',').map(s => s.trim());
                const checks = (row.checked        || '').split(',').map(s => s.trim());
                ids.forEach((id, i) => {
                    if (checks[i] === 'true' && !seenIds.has(id)) {
                        seenIds.add(id);
                        subjects.push({ subject_ids: id, subject_names: names[i] });
                    }
                });
            });
            const authorizedSubjects = filterByAuthorizedIds(subjects, getAuthorizedSubjectIds(), 'subject_ids');
            setClassSubjects(authorizedSubjects);
            setLoadingSubjects(false);
        }).catch(() => { setClassSubjects([]); setLoadingSubjects(false); });
    };

    useEffect(() => {
        const institutionId = parseInt(Cookies.get('institutionid'));

        client.query({ query: GET_ACADEMIC_YEARS, variables: { id: 1 }, fetchPolicy: 'network-only' })
            .then(res => {
                const years = res.data?.getAcademicYears || [];
                setAcademicYears(years);
                const now = new Date(); const yr = now.getFullYear(); const mo = now.getMonth() + 1;
                const ayStart = mo >= 4 ? yr : yr - 1;
                const currentAY = `${ayStart}-${String(ayStart + 1).slice(-2)}`;
                const def = years.find(y => y.academic_year === currentAY) || years[0];
                if (def) setSelYear(def.id);
            })
            .catch(() => {});

        client.query({ query: GET_BRANCHES, variables: { institution_id: institutionId }, fetchPolicy: 'network-only' })
            .then(res => {
                // Narrowed to the header's (NavbarOne) branch scope, which never
                // widens past the branches the user is authorized for.
                const data = scopeBranches(res.data?.getBranches || []);
                setBranches(data);
                if (editing?.branch_id) {
                    // Editing keeps the assignment's own branch — already known, never asked.
                    setSelBranch(Number(editing.branch_id));
                    // Re-apply sections as Numbers once branch data is available
                    if (editing.groupIds?.length) {
                        setSelSections(editing.groupIds.map(Number));
                    }
                } else if (data.length > 0) {
                    setSelBranch(data[0].id);
                }
                setLoadingBr(false);
            })
            .catch(() => setLoadingBr(false));

        client.query({ query: GET_CLASSES, variables: { id: 1 }, fetchPolicy: 'network-only' })
            .then(res => { setClasses(res.data?.getExamClasses || []); })
            .catch(() => {});

        client.query({ query: GET_STREAMS, fetchPolicy: 'network-only' })
            .then(res => { setAllStreams(res.data?.getStreams || []); })
            .catch(() => {});

    }, []);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    // All sections belonging to the selected branch
    const branchSections = selBranch
        ? (branches.find(b => Number(b.id) === Number(selBranch))?.sections || [])
        : [];

    // Class IDs present in the selected branch
    const branchClassIds = new Set(branchSections.map(s => Number(s.class_id)));

    // Class options: getExamClasses filtered to branch's class_ids and user authorization → { value, label }
    const authorizedClasses = filterByAuthorizedIds(classes, getAuthorizedClassIds(), 'id');
    const classOptions = authorizedClasses
        .filter(c => branchClassIds.size === 0 || branchClassIds.has(Number(c.id)))
        .map(c => ({ value: c.id, label: c.class }));

    // Stream options for selected class in form
    const formStreamOptions = (() => {
        const selectedClassId = selClasses[0];
        if (!selectedClassId || !isStreamClass(selectedClassId, classes)) return [];
        const classSecs = branchSections.filter(s => Number(s.class_id) === Number(selectedClassId));
        const uniqueStreamIds = [...new Set(classSecs.filter(s => s.stream_id != null).map(s => Number(s.stream_id)))];
        if (uniqueStreamIds.length > 0) {
            return uniqueStreamIds.map(stId => {
                const found = allStreams.find(st => Number(st.id) === Number(stId));
                return { value: stId, label: found ? found.stream : `Stream ${stId}` };
            });
        }
        if (allStreams && allStreams.length > 0) {
            return allStreams.map(st => ({ value: Number(st.id), label: st.stream }));
        }
        return [];
    })();
    const showFormStream = formStreamOptions.length > 0;

    useEffect(() => {
        if (formStreamOptions.length > 0) {
            const vals = formStreamOptions.map(o => o.value);
            if (selStreams.length === 0) {
                setSelStreams(vals);
            }
        }
    }, [formStreamOptions.map(o => o.value).join(',')]);

    // Section options: branch sections filtered by selected class_ids, stream, and user authorization → { value, label }
    // value is Number to match the integer IDs stored in selSections (from groupIds via parseInt)
    const authorizedSections = filterByAuthorizedIds(branchSections, getAuthorizedSectionIds(), 'id');
    const sectionOptions = authorizedSections
        .filter(s => !selClasses.length || selClasses.map(Number).includes(Number(s.class_id)))
        .filter(s => !showFormStream || !selStreams.length || s.stream_id == null || selStreams.map(Number).includes(Number(s.stream_id)))
        .map(s => ({ value: Number(s.id), label: s.section_name }));

    // groupIds = selected section IDs (sections are the assignment groups)
    const applyGroupIds = (sectionIds) => set('groupIds', sectionIds);

    // When editing and year + class are both available, auto-load the assignment's subjects
    useEffect(() => {
        if (editing?.class_id && selYear && selClasses.length) {
            fetchClassSubjects(selClasses[0], selYear);
        }
    }, [selYear, selClasses[0]]); // eslint-disable-line react-hooks/exhaustive-deps

    // When editing and subjects finish loading, auto-load faculty for the pre-selected subject
    useEffect(() => {
        if (!editing || !form.subject || !classSubjects.length || !selBranch) return;
        const selSubj = classSubjects.find(s => s.subject_names === form.subject);
        if (selSubj) fetchFaculty(selSubj.subject_ids);
    }, [classSubjects.length, selBranch]); // eslint-disable-line react-hooks/exhaustive-deps

    // When faculty list loads (after above effect), pre-select the faculty from editing.employee_id
    useEffect(() => {
        if (!editing?.employee_id || !faculty.length || form.faculty) return;
        const emp = faculty.find(f => Number(f.employeeId) === Number(editing.employee_id));
        if (emp) {
            set('faculty',     emp.employee_name);
            set('employee_id', String(emp.employeeId));
        }
    }, [faculty.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // Pass ISO date string (YYYY-MM-DD) directly — resolver calls new Date(str) → Unix ts
    const fmtMutDate = (d) => d || '';

    const save = () => {
        // Last gate before ADD_ASSIGNMENT / UPDATE_ASSIGNMENT. Checked here and not only
        // at the tab that opens this form: /assignment/create can be typed straight into
        // the address bar, which mounts this form without ever going through goToTab.
        // Ahead of the field validation, so a role without is_edit is told they lack
        // access rather than being sent to fix a form they can't submit.
        if (!guard.requireEdit()) return;

        if (uploading) return setToast('Please wait — file is still uploading.');

        const errs = {};
        if (!selYear)                    errs.year    = 'Academic year is required.';
        if (!selBranch)                  errs.branch  = 'Branch is required.';
        if (!selClasses.length)          errs.class   = 'Class is required.';
        if (!form.groupIds.length)       errs.section = 'Please select at least one section.';
        if (!form.subject)               errs.subject = 'Subject is required.';
        if (!form.title.trim())          errs.title   = 'Assignment title is required.';
        if (!form.maxMarks) {
            errs.maxMarks = 'Max marks is required.';
        } else {
            const mv = parseInt(form.maxMarks, 10);
            if (isNaN(mv) || mv < 1 || mv > 999) errs.maxMarks = 'Max marks must be between 1 and 999.';
        }
        if (!form.startDate)             errs.startDate = 'Start date is required.';
        if (!form.dueDate)               errs.dueDate   = 'End date is required.';
        if (form.startDate && form.dueDate && form.startDate > form.dueDate)
            errs.dueDate = 'End date must be on or after start date.';

        setErrors(errs);
        if (Object.keys(errs).length > 0) return;

        // ── No-change detection when editing ─────────────────────────────────────
        if (editing) {
            const currentSnap = JSON.stringify({
                title:       form.title,
                subject:     form.subject,
                description: form.description,
                maxMarks:    form.maxMarks,
                startDate:   form.startDate,
                dueDate:     form.dueDate,
                status:      form.status,
                groupIds:    [...form.groupIds].sort((a,b)=>a-b),
                filePath:    form.filePath || '',
            });
            if (currentSnap === initialSnapshot.current) {
                setToast('No changes detected. Please modify at least one field to update.');
                return;
            }
        }

        // estatus: 1 = active, 2 = draft, 0 = delete
        const estatus = form.status === 'draft' ? 2 : 1;

        const selSubj = classSubjects.find(s => s.subject_names === form.subject);
        const institutionId = parseInt(Cookies.get('institutionid'));

        setSaving(true);
        const mutationVars = editing?.id
            ? {
                  // ── UPDATE ──────────────────────────────────────────────────
                  mutation: UPDATE_ASSIGNMENT,
                  variables: {
                      assignment_id:  editing.id,
                      title:          form.title,
                      description:    form.description || '',
                      file_path:      form.filePath || editing.file_path || '',
                      from_date:      fmtMutDate(form.startDate),
                      to_date:        fmtMutDate(form.dueDate),
                      assignment_marks: parseInt(form.maxMarks, 10),
                      employee_id:    form.employee_id ? parseInt(form.employee_id) : null,
                      branch_id:      selBranch ? parseInt(selBranch) : null,
                      class_id:       selClasses[0] ? String(selClasses[0]) : '',
                      subject_id:     selSubj ? parseInt(selSubj.subject_ids) : null,
                      section_ids:    selSections.join(', '),
                      estatus,
                  },
              }
            : {
                  // ── INSERT ──────────────────────────────────────────────────
                  mutation: ADD_ASSIGNMENT,
                  variables: {
                      title:                    form.title,
                      description:              form.description || '',
                      mediafiles_mapping_id:    0,
                      file_path:                form.filePath || '',
                      dairy_assignment_type_id: 2,
                      from_date:                fmtMutDate(form.startDate),
                      to_date:                  fmtMutDate(form.dueDate),
                      assignment_marks:         parseInt(form.maxMarks, 10),
                      employee_id:              form.employee_id ? parseInt(form.employee_id) : null,
                      institute_id:             institutionId,
                      branch_id:                selBranch ? parseInt(selBranch) : null,
                      class_id:                 selClasses[0] ? String(selClasses[0]) : '',
                      subject_id:               selSubj ? parseInt(selSubj.subject_ids) : null,
                      section_ids:              selSections.join(', '),
                      estatus,
                  },
              };

        client.mutate(mutationVars).then((res) => {
            setSaving(false);
            setSuccess(true);
            // For new assignments the resolver returns the new assignment id (Int)
            const newId = editing?.id
                ? Number(editing.id)
                : Number(res.data?.addStudentAssignment) || null;
            setTimeout(() => {
                onSave({ ...form, maxMarks: parseInt(form.maxMarks, 10), id: newId });
            }, 2500);
        }).catch(() => { setSaving(false); setToast('Failed to save assignment. Please try again.'); });
    };

    const inp = { padding:'9px 12px',border:'1.5px solid #e2e8f0',borderRadius:10,fontSize:13,color:'#1f2937',outline:'none',width:'100%',background:'#fff' };

    return (
        <div style={{background:'#fff',borderRadius:16,border:'1px solid #e2e8f0',boxShadow:'0 4px 20px rgba(0,0,0,0.08)'}}>
            <div style={{background:'linear-gradient(135deg,#ecfdf5,#d1fae5)',padding:'20px 26px',display:'flex',alignItems:'center',gap:12,borderRadius:'16px 16px 0 0',borderBottom:'1.5px solid #a7f3d0'}}>
                <div style={{width:42,height:42,background:'#059669',borderRadius:11,display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <i className={`fas ${editing?'fa-pen':'fa-plus-circle'}`} style={{color:'#fff',fontSize:18}} />
                </div>
                <div>
                    <div style={{color:'#064e3b',fontWeight:800,fontSize:17}}>{editing ? 'Edit Assignment' : 'Create New Assignment'}</div>
                    <div style={{color:'#059669',fontSize:12,marginTop:2}}>Fill all required fields and assign to groups</div>
                </div>
            </div>

            {/* ── Edit success: fixed top popup ─────────────────────────────────── */}
            {success && editing && (
                <div style={{position:'fixed',top:0,left:0,right:0,zIndex:99999,background:'linear-gradient(135deg,#10b981,#059669)',padding:'14px 32px',display:'flex',alignItems:'center',justifyContent:'space-between',boxShadow:'0 4px 24px rgba(16,185,129,0.45)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:14,color:'#fff'}}>
                        <div style={{width:40,height:40,background:'rgba(255,255,255,0.2)',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                            <i className="fas fa-check" style={{fontSize:18}} />
                        </div>
                        <div>
                            <div style={{fontWeight:800,fontSize:16,marginBottom:2}}>Assignment Updated Successfully!</div>
                            <div style={{fontSize:12,opacity:0.88}}>"{form.title}" has been saved. Redirecting to dashboard…</div>
                        </div>
                    </div>
                    <i className="fas fa-spinner fa-spin" style={{color:'rgba(255,255,255,0.75)',fontSize:18,flexShrink:0}} />
                </div>
            )}


            <div style={{padding:26}}>
                {toast && (
                    <div style={{background:'#fee2e2',border:'1.5px solid #fca5a5',borderRadius:10,padding:'10px 16px',marginBottom:16,fontSize:13,fontWeight:700,color:'#dc2626'}}>
                        <i className="fas fa-exclamation-circle" style={{marginRight:7}} />{toast}
                    </div>
                )}

                {/* helper: render inline error */}
                {/* Row 1: Academic Year → Branch → Class → (Stream) → Section */}
                <Row style={{marginBottom:14,rowGap:14}}>
                    <Col md={showFormStream ? 2 : 3}>
                        <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:5,whiteSpace:'nowrap'}}>Academic Year *</div>
                        <select
                            value={selYear || ''}
                            onChange={e => {
                                const newYear = e.target.value;
                                setSelYear(newYear);
                                set('subject', ''); set('faculty', '');
                                fetchClassSubjects(selClasses[0], newYear);
                                setErrors(p=>({...p,year:undefined}));
                            }}
                            style={{...inp,border:errors.year?'1.5px solid #ef4444':inp.border}}
                        >
                            {academicYears.length === 0 && <option value="">Loading…</option>}
                            {academicYears.map(y => <option key={y.id} value={y.id}>{y.academic_year}</option>)}
                        </select>
                        {errors.year && <div style={{color:'#ef4444',fontSize:11,marginTop:3}}>{errors.year}</div>}
                    </Col>
                    {/* DATA-ENTRY: saved as branch_id on the assignment, and Class /
                        Section / Subject / Faculty all cascade from it — so the dropdown
                        lives here on the form. The header only narrows the options. */}
                    <Col md={showFormStream ? 2 : 3}>
                        <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:5}}>Branch *</div>
                        <select
                            value={selBranch || ''}
                            onChange={e => {
                                const val = parseInt(e.target.value) || null;
                                setSelBranch(val);
                                setSelClasses([]);
                                setSelStreams([]);
                                setSelSections([]);
                                applyGroupIds([]);
                                setClassSubjects([]); setFaculty([]);
                                set('subject', ''); set('faculty', '');
                                setErrors(p=>({...p,branch:undefined}));
                            }}
                            style={{...inp,border:errors.branch?'1.5px solid #ef4444':inp.border}}
                            disabled={loadingBr}
                        >
                            {loadingBr && <option value="">Loading…</option>}
                            {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
                        </select>
                        {errors.branch && <div style={{color:'#ef4444',fontSize:11,marginTop:3}}>{errors.branch}</div>}
                    </Col>
                    <Col md={showFormStream ? 2 : 3}>
                        <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:5}}>Class *</div>
                        <select
                            value={selClasses[0] || ''}
                            onChange={e => {
                                const val = e.target.value ? Number(e.target.value) : null;
                                setSelClasses(val ? [val] : []);
                                let newStreams = [];
                                if (val && isStreamClass(val, classes)) {
                                    const classSecs = branchSections.filter(s => Number(s.class_id) === Number(val));
                                    const uniqueStreamIds = [...new Set(classSecs.filter(s => s.stream_id != null).map(s => Number(s.stream_id)))];
                                    if (uniqueStreamIds.length > 0) {
                                        newStreams = uniqueStreamIds;
                                    } else if (allStreams.length > 0) {
                                        newStreams = allStreams.map(st => Number(st.id));
                                    }
                                }
                                setSelStreams(newStreams);
                                const newSections = branchSections
                                    .filter(s => val ? Number(s.class_id) === val : false)
                                    .filter(s => !newStreams.length || s.stream_id == null || newStreams.map(Number).includes(Number(s.stream_id)))
                                    .map(s => Number(s.id));
                                setSelSections(newSections);
                                applyGroupIds(newSections);
                                set('subject', ''); set('faculty', '');
                                setFaculty([]);
                                fetchClassSubjects(val, selYear);
                                setErrors(p=>({...p,class:undefined,section:undefined}));
                            }}
                            style={{...inp,border:errors.class?'1.5px solid #ef4444':inp.border,opacity:!selBranch?0.6:1}}
                            disabled={!selBranch}
                        >
                            <option value="">Select Class</option>
                            {classOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                        {errors.class && <div style={{color:'#ef4444',fontSize:11,marginTop:3}}>{errors.class}</div>}
                    </Col>
                    {showFormStream && (
                        <Col md={3}>
                            <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:5}}>Stream *</div>
                            <CheckboxDropdown
                                options={formStreamOptions}
                                value={selStreams}
                                onChange={val => {
                                    setSelStreams(val);
                                    const updatedSections = branchSections
                                        .filter(s => selClasses[0] ? Number(s.class_id) === selClasses[0] : false)
                                        .filter(s => !val.length || s.stream_id == null || val.map(Number).includes(Number(s.stream_id)))
                                        .map(s => Number(s.id));
                                    setSelSections(updatedSections);
                                    applyGroupIds(updatedSections);
                                }}
                                placeholder="Select Stream"
                                disabled={!selClasses.length}
                            />
                        </Col>
                    )}
                    <Col md={showFormStream ? 3 : 3}>
                        <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:5}}>Section *</div>
                        <CheckboxDropdown
                            options={sectionOptions}
                            value={selSections}
                            onChange={val => {
                                setSelSections(val);
                                applyGroupIds(val);
                                setErrors(p=>({...p,section:undefined}));
                            }}
                            placeholder={selClasses.length ? 'Select Sections' : 'Select class first'}
                            disabled={!selClasses.length}
                        />
                        {errors.section && <div style={{color:'#ef4444',fontSize:11,marginTop:3}}>{errors.section}</div>}
                    </Col>
                </Row>

                {/* Row 2: Subject → Faculty → Title → Max Marks */}
                <Row style={{marginBottom:14}}>
                    <Col md={3}>
                        <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:5,whiteSpace:'nowrap'}}>Subject *</div>
                        <select value={form.subject} onChange={e=>{
                            const subjectName = e.target.value;
                            set('subject', subjectName);
                            set('faculty', ''); setFaculty([]);
                            if (subjectName) {
                                const selSubj = classSubjects.find(s => s.subject_names === subjectName);
                                if (selSubj) fetchFaculty(selSubj.subject_ids);
                            }
                            setErrors(p=>({...p,subject:undefined}));
                        }} style={{...inp,border:errors.subject?'1.5px solid #ef4444':inp.border,color:form.subject?'#1f2937':'#9ca3af'}} disabled={!selClasses.length||loadingSubjects}>
                            <option value="">{!selClasses.length?'Select class first':loadingSubjects?'Loading…':'Select Subject'}</option>
                            {classSubjects.map(s=><option key={s.subject_ids} value={s.subject_names}>{s.subject_names}</option>)}
                        </select>
                        {errors.subject && <div style={{color:'#ef4444',fontSize:11,marginTop:3}}>{errors.subject}</div>}
                    </Col>
                    <Col md={3}>
                        <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:5,whiteSpace:'nowrap'}}>Subject Faculty</div>
                        <select key={form.subject} value={form.faculty} onChange={e=>{
                            const empName = e.target.value;
                            set('faculty', empName);
                            const emp = faculty.find(f=>f.employee_name===empName);
                            set('employee_id', emp?emp.employeeId:'');
                        }} style={{...inp,color:form.faculty?'#1f2937':'#9ca3af'}} disabled={!form.subject||loadingFaculty}>
                            <option value="">{!form.subject?'Select subject first':loadingFaculty?'Loading…':'Select Faculty'}</option>
                            {faculty.map((emp,i)=><option key={i} value={emp.employee_name}>{emp.employee_name}</option>)}
                        </select>
                    </Col>
                    <Col md={4}>
                        <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:5}}>Assignment Title *</div>
                        <input
                            value={form.title}
                            onChange={e=>{ if(e.target.value.length<=100){set('title',e.target.value);setErrors(p=>({...p,title:undefined}));} }}
                            maxLength={100}
                            placeholder="e.g. Newton's Laws – Problem Set"
                            style={{...inp,border:errors.title?'1.5px solid #ef4444':inp.border}}
                        />
                        <div style={{display:'flex',justifyContent:'space-between',marginTop:3}}>
                            {errors.title ? <span style={{color:'#ef4444',fontSize:11}}>{errors.title}</span> : <span/>}
                            <span style={{fontSize:10,color:'#94a3b8'}}>{form.title.length}/100</span>
                        </div>
                    </Col>
                    <Col md={2}>
                        <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:5}}>Max Marks *</div>
                        <input
                            type="number" min="1" max="999"
                            value={form.maxMarks}
                            onChange={e=>{
                                const v = e.target.value;
                                if(v===''||(/^\d{1,3}$/.test(v)&&parseInt(v,10)<=999)){
                                    set('maxMarks',v);
                                    setErrors(p=>({...p,maxMarks:undefined}));
                                }
                            }}
                            placeholder="50"
                            style={{...inp,border:errors.maxMarks?'1.5px solid #ef4444':inp.border}}
                        />
                        {errors.maxMarks && <div style={{color:'#ef4444',fontSize:11,marginTop:3}}>{errors.maxMarks}</div>}
                    </Col>
                </Row>

                {/* Row 3: Description (optional) + Upload (optional) */}
                <Row style={{marginBottom:14}}>
                    <Col md={6}>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
                            <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px'}}>Description <span style={{color:'#9ca3af',fontWeight:400,textTransform:'none',fontSize:10}}>(optional)</span></div>
                            <span style={{fontSize:10,color:'#94a3b8'}}>{(form.description||'').length}/1000</span>
                        </div>
                        <textarea
                            value={form.description}
                            onChange={e=>{ if(e.target.value.length<=1000) set('description',e.target.value); }}
                            maxLength={1000}
                            placeholder="What does this assignment cover?"
                            rows={3}
                            style={{...inp,resize:'vertical'}}
                        />
                    </Col>
                    <Col md={6}>
                        <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:10}}>
                            <i className="fas fa-paperclip" style={{marginRight:6,color:PURPLE}} />Upload File <span style={{color:'#9ca3af',fontWeight:400,textTransform:'none',fontSize:10}}>(optional)</span>
                        </div>
                        <label htmlFor="assignment-file-upload"
                            style={{display:'flex',alignItems:'center',gap:14,border:'2px dashed #d1d5db',borderRadius:10,padding:'16px 20px',cursor:'pointer',background:'#fafafe',transition:'all 0.2s'}}
                            onMouseEnter={e=>{e.currentTarget.style.borderColor=PURPLE;e.currentTarget.style.background=`${PURPLE}08`;}}
                            onMouseLeave={e=>{e.currentTarget.style.borderColor='#d1d5db';e.currentTarget.style.background='#fafafe';}}>
                            <div style={{width:42,height:42,background:`${PURPLE}15`,borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                <i className="fas fa-cloud-upload-alt" style={{color:PURPLE,fontSize:18}} />
                            </div>
                            <div style={{flex:1}}>
                                {form.fileName ? (
                                    <div>
                                        <div style={{fontWeight:700,fontSize:13,color:'#1f2937'}}>{form.fileName}</div>
                                        {uploading ? <div style={{fontSize:11,color:PURPLE,marginTop:2}}><i className="fas fa-spinner fa-spin" style={{marginRight:4}} />Uploading…</div>
                                         : uploadError ? <div style={{fontSize:11,color:'#f59e0b',marginTop:2}}><i className="fas fa-exclamation-triangle" style={{marginRight:4}} />{uploadError}</div>
                                         : form.filePath ? <div style={{fontSize:11,color:'#10b981',marginTop:2}}><i className="fas fa-check-circle" style={{marginRight:4}} />Uploaded successfully</div>
                                         : <div style={{fontSize:11,color:'#10b981',marginTop:2}}><i className="fas fa-check-circle" style={{marginRight:4}} />File selected</div>}
                                    </div>
                                ) : (
                                    <div>
                                        <div style={{fontWeight:700,fontSize:13,color:'#374151'}}>Drop file here or click to browse</div>
                                        <div style={{fontSize:11,color:'#9ca3af',marginTop:2}}>PDF, Word (.doc/.docx), Images — max 50 MB</div>
                                    </div>
                                )}
                            </div>
                            {form.fileName && (
                                <button type="button" onClick={e=>{e.preventDefault();e.stopPropagation();set('fileName','');set('fileData',null);set('filePath','');setUploadError('');}}
                                    style={{width:28,height:28,borderRadius:'50%',border:'none',background:'#fee2e2',color:'#dc2626',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                    <i className="fas fa-times" style={{fontSize:11}} />
                                </button>
                            )}
                        </label>
                        <input id="assignment-file-upload" type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.zip" style={{display:'none'}}
                            onChange={e => {
                                const file = e.target.files[0];
                                if (!file) return;
                                // Picking a file fires FILE_UPLOAD immediately, so this is
                                // a write → is_edit. The input stays enabled; the upload is
                                // what gets stopped. Reset it so the rejected file isn't
                                // left sitting in the control.
                                if (!guard.requireEdit()) { e.target.value = ''; return; }
                                set('fileName', file.name); set('fileData', file);
                                setUploadError(''); setUploading(true);
                                const reader = new FileReader();
                                reader.onload = () => {
                                    const base64 = reader.result;
                                    const institutionId = parseInt(Cookies.get('institutionid'));
                                    client.mutate({
                                        mutation: FILE_UPLOAD,
                                        variables: { category:'assignment', file:[{img:base64,filename:file.name}], client_id:institutionId },
                                    }).then(res => {
                                        const result = res.data?.fileUpload?.[0] || {};
                                        const url = result.file_url || '';
                                        const storedName = result.file_name || file.name;
                                        set('filePath', url ? `${url}#${encodeURIComponent(storedName)}` : '');
                                        setUploading(false);
                                    }).catch(err => {
                                        console.error('File upload error:', err);
                                        setUploadError('Upload failed. File name saved only.');
                                        set('filePath', ''); setUploading(false);
                                    });
                                };
                                reader.readAsDataURL(file);
                                e.target.value = '';
                            }} />
                    </Col>
                </Row>

                {/* Row 4: Start Date → End Date → Status */}
                <Row style={{marginBottom:14}}>
                    <Col md={4}>
                        <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:5}}>Start Date *</div>
                        <input type="date" value={form.startDate}
                            onChange={e=>{set('startDate',e.target.value);setErrors(p=>({...p,startDate:undefined,dueDate:undefined}));}}
                            style={{...inp,border:errors.startDate?'1.5px solid #ef4444':inp.border}} />
                        {errors.startDate && <div style={{color:'#ef4444',fontSize:11,marginTop:3}}>{errors.startDate}</div>}
                    </Col>
                    <Col md={4}>
                        <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:5}}>End Date *</div>
                        <input type="date" value={form.dueDate}
                            onChange={e=>{set('dueDate',e.target.value);setErrors(p=>({...p,dueDate:undefined}));}}
                            style={{...inp,border:errors.dueDate?'1.5px solid #ef4444':inp.border}} />
                        {errors.dueDate && <div style={{color:'#ef4444',fontSize:11,marginTop:3}}>{errors.dueDate}</div>}
                    </Col>
                    <Col md={4}>
                        <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:5}}>Status *</div>
                        <select value={form.status} onChange={e=>set('status',e.target.value)} style={inp}>
                            <option value="active">Active (visible to students)</option>
                            <option value="draft">Draft (hidden)</option>
                        </select>
                    </Col>
                </Row>

                {/* Success message above buttons (#10) */}
                {success && (
                    <div style={{background:'linear-gradient(135deg,#d1fae5,#a7f3d0)',border:'1.5px solid #6ee7b7',borderRadius:10,padding:'12px 18px',marginBottom:12,display:'flex',alignItems:'center',gap:12}}>
                        <div style={{width:32,height:32,background:'#10b981',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                            <i className="fas fa-check" style={{color:'#fff',fontSize:14}} />
                        </div>
                        <div style={{flex:1}}>
                            <div style={{fontSize:14,fontWeight:800,color:'#065f46'}}>
                                Assignment {form.status==='draft'?'Saved as Draft':'Published'} Successfully!
                            </div>
                            <div style={{fontSize:12,color:'#047857',marginTop:2}}>
                                <strong>{form.title}</strong> — redirecting…
                            </div>
                        </div>
                        <i className="fas fa-spinner fa-spin" style={{color:'#10b981',fontSize:14,flexShrink:0}} />
                    </div>
                )}

                <div style={{display:'flex',gap:10,justifyContent:'flex-end',paddingTop:8,borderTop:'1px solid #f1f5f9'}}>
                    <GhostBtn onClick={onCancel} disabled={saving||success}>Cancel</GhostBtn>
                    <PrimaryBtn onClick={save} disabled={saving||success}
                        style={{background:form.status==='draft'?'linear-gradient(135deg,#64748b,#475569)':`linear-gradient(135deg,${PURPLE},#8b5cf6)`}}>
                        {saving
                            ? <><i className="fas fa-spinner fa-spin" /> Saving…</>
                            : form.status==='draft'
                                ? <><i className="fas fa-save" /> {editing?'Save Draft':'Save as Draft'}</>
                                : <><i className={`fas ${editing?'fa-save':'fa-rocket'}`} /> {editing?'Save Changes':'Publish Assignment'}</>
                        }
                    </PrimaryBtn>
                </div>
            </div>

        </div>
    );
}

// ─── Teacher: Dashboard ───────────────────────────────────────────────────────
function TeacherDashboard({ assignments, submissions, groups, students, onTabChange, onDeleteAssignment, onGrade, onEdit, loading }) {
    // Assignment-level counts — all-or-nothing per assignment
    const submittedCount = assignments.filter(a => {
        const rows = submissions.filter(s => Number(s.dairy_assignment_id) === Number(a.id));
        // Submitted: every student has submitted (none still pending)
        return rows.length > 0 && rows.every(s => s.status === 1 || s.status === 2);
    }).length;
    const pendingCount = assignments.filter(a =>
        // Pending: at least one student has not submitted yet
        submissions.some(s => Number(s.dairy_assignment_id) === Number(a.id) && s.status === 0)
    ).length;
    const gradedCount = assignments.filter(a => {
        const rows = submissions.filter(s => Number(s.dairy_assignment_id) === Number(a.id));
        // Graded: every student's work has been reviewed and marked
        return rows.length > 0 && rows.every(s => s.status === 2);
    }).length;
    const [viewAssignment,  setViewAssignment]  = useState(null);
    const [showPdfPreview,  setShowPdfPreview]  = useState(false);
    const [deleteConfirm,   setDeleteConfirm]   = useState(null);
    const [page,     setPage]     = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search,   setSearch]   = useState('');
    const [filter,   setFilter]   = useState('all');

    const PAGE_SIZE = pageSize;
    const filtered  = assignments.filter(a => {
        if (filter !== 'all' && a.status !== filter) return false;
        if (search) {
            const q = search.toLowerCase();
            const match = a.title?.toLowerCase().includes(q)
                || a.subject?.toLowerCase().includes(q)
                || a.class_name?.toLowerCase().includes(q)
                || a.Section_Name?.toLowerCase().includes(q);
            if (!match) return false;
        }
        return true;
    });
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paged      = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const countFor   = s => s === 'all' ? assignments.length : assignments.filter(a => a.status === s).length;

    return (
        <div>
            <Row style={{rowGap:0}}>
                <Col md={3} sm={6}><StatCard icon="fa-file-alt"      label="Assignments" value={assignments.length} color={PURPLE}  bg="#ede9fe" loading={loading} /></Col>
                <Col md={3} sm={6}><StatCard icon="fa-paper-plane"   label="Submitted"   value={submittedCount}     color="#3b82f6" bg="#dbeafe" loading={loading} /></Col>
                <Col md={3} sm={6}><StatCard icon="fa-clock"         label="Pending"     value={pendingCount}       color="#f59e0b" bg="#fef3c7" loading={loading} /></Col>
                <Col md={3} sm={6}><StatCard icon="fa-check-circle"  label="Graded"      value={gradedCount}        color="#10b981" bg="#d1fae5" loading={loading} /></Col>
            </Row>

            {/* Recent assignments — table */}
            <div style={{background:'#fff',borderRadius:16,border:'1px solid #e2e8f0',overflow:'hidden',boxShadow:'0 2px 12px rgba(0,0,0,0.05)',marginBottom:20}}>
                <div style={{padding:'14px 18px',borderBottom:'1px solid #f1f5f9',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:10}}>
                    <span style={{fontWeight:700,fontSize:15,color:'#1f2937',display:'flex',alignItems:'center',gap:10}}>
                        <i className="fas fa-table" style={{color:PURPLE}} /> Assignments
                    </span>
                </div>

                {/* Search + Filter bar */}
                <div style={{padding:'12px 18px',borderBottom:'1px solid #f1f5f9',display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                    {/* Search */}
                    <div style={{position:'relative',flex:1,minWidth:200,maxWidth:340}}>
                        <i className="fas fa-search" style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'#9ca3af',fontSize:12}} />
                        <input
                            value={search}
                            onChange={e=>{setSearch(e.target.value);setPage(1);}}
                            placeholder="Search by title, subject, class or section…"
                            style={{width:'100%',padding:'8px 12px 8px 34px',border:'1.5px solid #e2e8f0',borderRadius:10,fontSize:13,outline:'none',background:'#f9fafb',color:'#1f2937'}}
                        />
                    </div>

                    {/* Filter tabs */}
                    <div style={{display:'flex',background:'#f1f5f9',borderRadius:10,padding:4,gap:2,border:'1px solid #e2e8f0'}}>
                        {['all','active','draft'].map(f=>(
                            <button key={f} onClick={()=>{setFilter(f);setPage(1);}}
                                style={{padding:'6px 14px',borderRadius:8,border:'none',fontSize:12,fontWeight:700,cursor:'pointer',background:filter===f?PURPLE:'transparent',color:filter===f?'#fff':'#64748b',textTransform:'capitalize',display:'flex',alignItems:'center',gap:5,transition:'all 0.15s'}}>
                                {f}
                                <span style={{background:filter===f?'rgba(255,255,255,0.25)':'#e2e8f0',color:filter===f?'#fff':'#64748b',borderRadius:20,padding:'1px 7px',fontSize:10,fontWeight:800,minWidth:18,textAlign:'center'}}>
                                    {countFor(f)}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
                {(() => {
                    const cols = ['#','Title','Subject','Branch','Class','Section','Max Marks','Start Date','End Date','Submission','Status','Actions'];
                    const actionIdx = cols.length - 1;
                    if (loading) return <ShimmerTable widths={[30,170,90,90,70,80,80,90,90,110,80,150]} rows={8} />;
                    return (
                        <>
                        <div style={{overflowX:'auto'}}>
                        <table style={{width:'100%',borderCollapse:'collapse',minWidth:900}}>
                            <thead>
                                <tr style={{background:'linear-gradient(135deg,#f5f3ff,#f0f9ff)'}}>
                                    {cols.map((h,i)=>(
                                        <th key={h} style={{padding:'11px 14px',fontSize:11,fontWeight:700,color:'#475569',textTransform:'uppercase',letterSpacing:'0.4px',textAlign:(i===0||i===actionIdx)?'center':'left',whiteSpace:'nowrap',borderBottom:'2px solid #e2e8f0',...(i===actionIdx?{position:'sticky',right:0,zIndex:3,background:'#f3f0ff',boxShadow:'-4px 0 6px -4px rgba(0,0,0,0.12)'}:{})}}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {paged.length === 0 && (
                                    <tr>
                                        <td colSpan={cols.length} style={{padding:'40px',textAlign:'center',color:'#94a3b8',fontSize:13}}>
                                            <i className="fas fa-file-alt" style={{fontSize:28,display:'block',marginBottom:8}} />
                                            {search || filter !== 'all' ? 'No assignments match your search or filter' : 'No assignments found'}
                                        </td>
                                    </tr>
                                )}
                                {paged.map((a,i) => {
                                    // Every student in the assignment's selected sections gets a status row
                                    // (status 0/1/2) at creation — so their count is the true total.
                                    const allRows = submissions.filter(s=>Number(s.dairy_assignment_id)===Number(a.id));
                                    const subs    = allRows.filter(s=>s.status===1 || s.status===2);
                                    const dl      = daysLeft(a.dueDate);
                                    const c       = colorOf(a.subject||'x');
                                    const total   = allRows.length || a.student_count || 0;
                                    const pct     = total ? Math.min(100, Math.round(subs.length / total * 100)) : 0;
                                    const rowNum = (page-1)*PAGE_SIZE + i + 1;
                                    return (
                                        <tr key={a.id} style={{borderBottom:'1px solid #f1f5f9',background:i%2===0?'#fff':'#fafafe'}}>
                                            <td style={{padding:'11px 14px',textAlign:'center',fontSize:12,color:'#94a3b8',fontWeight:600}}>{rowNum}</td>

                                            <td style={{padding:'11px 14px',minWidth:180}}>
                                                <div style={{display:'flex',alignItems:'center',gap:9}}>
                                                    <div style={{width:30,height:30,background:c+'18',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                                        <i className="fas fa-book-open" style={{color:c,fontSize:12}} />
                                                    </div>
                                                    <div style={{fontWeight:700,fontSize:13,color:'#1f2937',lineHeight:1.3}}>{a.title}</div>
                                                </div>
                                            </td>

                                            <td style={{padding:'11px 14px',whiteSpace:'nowrap'}}>
                                                <SubjectChip subject={a.subject} />
                                            </td>

                                            <td style={{padding:'11px 14px',fontSize:12,color:'#374151',fontWeight:500,whiteSpace:'nowrap'}}>
                                                {a.branch_name || <span style={{color:'#cbd5e1'}}>—</span>}
                                            </td>

                                            <td style={{padding:'11px 14px',fontSize:12,color:'#374151',fontWeight:500,whiteSpace:'nowrap'}}>
                                                {a.class_name || <span style={{color:'#cbd5e1'}}>—</span>}
                                            </td>

                                            <td style={{padding:'11px 14px',fontSize:12,color:'#374151',fontWeight:500,whiteSpace:'nowrap'}}>
                                                {a.Section_Name || <span style={{color:'#cbd5e1'}}>—</span>}
                                            </td>

                                            <td style={{padding:'11px 14px',textAlign:'center',whiteSpace:'nowrap'}}>
                                                <span style={{display:'inline-flex',alignItems:'center',gap:4,background:'#ede9fe',color:'#6d28d9',borderRadius:6,padding:'3px 10px',fontSize:12,fontWeight:700}}>
                                                    <i className="fas fa-star" style={{fontSize:9}} />{a.maxMarks}
                                                </span>
                                            </td>

                                            <td style={{padding:'11px 14px',whiteSpace:'nowrap'}}>
                                                <div style={{fontSize:12,color:'#374151',fontWeight:500}}>{fmtDate(a.startDate)||'—'}</div>
                                            </td>

                                            <td style={{padding:'11px 14px',whiteSpace:'nowrap'}}>
                                                <div style={{fontSize:12,color:'#374151',fontWeight:500}}>{fmtDate(a.dueDate)||'—'}</div>
                                                {dl !== null && (
                                                    <div style={{fontSize:10,fontWeight:700,marginTop:2,color:dl<0?'#dc2626':dl<=2?'#f59e0b':'#10b981'}}>
                                                        {dl<0?`${Math.abs(dl)}d overdue`:dl===0?'Due today':`${dl}d left`}
                                                    </div>
                                                )}
                                            </td>

                                            <td style={{padding:'11px 14px',minWidth:120}}>
                                                <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}>
                                                    <span style={{fontSize:11,color:'#64748b',fontWeight:600}}>{subs.length}/{total}</span>
                                                    <span style={{fontSize:11,color:c,fontWeight:700}}>{pct}%</span>
                                                </div>
                                                <div style={{height:5,background:'#f1f5f9',borderRadius:10}}>
                                                    <div style={{width:`${pct}%`,height:'100%',background:c,borderRadius:10}} />
                                                </div>
                                            </td>

                                            <td style={{padding:'11px 14px',whiteSpace:'nowrap'}}>
                                                <Badge status={a.status} />
                                            </td>

                                            <td style={{padding:'11px 14px',textAlign:'center',whiteSpace:'nowrap',position:'sticky',right:0,zIndex:1,background:i%2===0?'#fff':'#fafafe',boxShadow:'-4px 0 6px -4px rgba(0,0,0,0.12)'}}>
                                                <div style={{display:'inline-flex',gap:6,alignItems:'center'}}>
                                                    {/* Review Submissions */}
                                                    <button onClick={()=>onGrade(a)} title="Review submissions"
                                                        style={{height:30,padding:'0 10px',borderRadius:7,border:`1.5px solid ${PURPLE}30`,background:`${PURPLE}12`,color:PURPLE,cursor:'pointer',display:'flex',alignItems:'center',gap:5,fontSize:11,fontWeight:700,whiteSpace:'nowrap',position:'relative'}}>
                                                        <i className="fas fa-clipboard-check" style={{fontSize:10}} />
                                                        Review
                                                        {(() => {
                                                            const pending = submissions.filter(s=>Number(s.dairy_assignment_id)===Number(a.id)&&s.status===1).length;
                                                            return pending > 0 ? (
                                                                <span style={{background:'#ef4444',color:'#fff',borderRadius:20,padding:'1px 5px',fontSize:9,fontWeight:800,lineHeight:'14px'}}>{pending}</span>
                                                            ) : null;
                                                        })()}
                                                    </button>
                                                    {/* View */}
                                                    <button onClick={()=>{setViewAssignment(a);setShowPdfPreview(false);}} title="View"
                                                        style={{width:30,height:30,borderRadius:7,border:'none',background:'#eff6ff',color:'#3b82f6',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                                                        <i className="fas fa-eye" style={{fontSize:11}} />
                                                    </button>
                                                    {/* Edit */}
                                                    <button onClick={()=>onEdit(a)} title="Edit"
                                                        style={{width:30,height:30,borderRadius:7,border:'none',background:'#eff6ff',color:'#3b82f6',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                                                        <i className="fas fa-edit" style={{fontSize:12}} />
                                                    </button>
                                                    {/* Delete */}
                                                    <button onClick={()=>setDeleteConfirm(a)} title="Delete"
                                                        style={{width:30,height:30,borderRadius:7,border:'none',background:'#fee2e2',color:'#dc2626',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                                                        <i className="fas fa-trash" style={{fontSize:11}} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>

                        {/* Pagination */}
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 18px',borderTop:'1px solid #f1f5f9',background:'#fafafa',flexWrap:'wrap',gap:8}}>
                            <div style={{display:'flex',alignItems:'center',gap:8}}>
                                <span style={{fontSize:12,color:'#64748b'}}>
                                    Showing <strong>{filtered.length===0?0:(page-1)*PAGE_SIZE+1}</strong>–<strong>{Math.min(page*PAGE_SIZE,filtered.length)}</strong> of <strong>{filtered.length}</strong> Results
                                </span>
                                <select
                                    value={pageSize}
                                    onChange={e=>{setPageSize(Number(e.target.value));setPage(1);}}
                                    style={{padding:'4px 8px',border:'1.5px solid #e2e8f0',borderRadius:7,fontSize:12,color:'#374151',background:'#fff',cursor:'pointer',outline:'none'}}>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={assignments.length||9999}>All</option>
                                </select>
                                <span style={{fontSize:12,color:'#94a3b8'}}>per page</span>
                            </div>
                            <div style={{display:'flex',gap:4,alignItems:'center'}}>
                                <button onClick={()=>setPage(1)} disabled={page===1}
                                    style={{width:30,height:30,borderRadius:7,border:'1px solid #e2e8f0',background:page===1?'#f8fafc':'#fff',color:page===1?'#cbd5e1':'#374151',cursor:page===1?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11}}>
                                    <i className="fas fa-angle-double-left" />
                                </button>
                                <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}
                                    style={{width:30,height:30,borderRadius:7,border:'1px solid #e2e8f0',background:page===1?'#f8fafc':'#fff',color:page===1?'#cbd5e1':'#374151',cursor:page===1?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11}}>
                                    <i className="fas fa-angle-left" />
                                </button>
                                {Array.from({length:totalPages},(_,k)=>k+1)
                                    .filter(p=>p===1||p===totalPages||Math.abs(p-page)<=1)
                                    .reduce((acc,p,idx,arr)=>{
                                        if(idx>0&&p-arr[idx-1]>1) acc.push('...');
                                        acc.push(p); return acc;
                                    },[])
                                    .map((p,idx)=> p==='...'
                                        ? <span key={'e'+idx} style={{padding:'0 4px',color:'#94a3b8',fontSize:12}}>…</span>
                                        : <button key={p} onClick={()=>setPage(p)}
                                            style={{width:30,height:30,borderRadius:7,border:`1px solid ${page===p?PURPLE:'#e2e8f0'}`,background:page===p?PURPLE:'#fff',color:page===p?'#fff':'#374151',cursor:'pointer',fontWeight:page===p?700:400,fontSize:12,display:'flex',alignItems:'center',justifyContent:'center'}}>
                                            {p}
                                          </button>
                                    )
                                }
                                <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}
                                    style={{width:30,height:30,borderRadius:7,border:'1px solid #e2e8f0',background:page===totalPages?'#f8fafc':'#fff',color:page===totalPages?'#cbd5e1':'#374151',cursor:page===totalPages?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11}}>
                                    <i className="fas fa-angle-right" />
                                </button>
                                <button onClick={()=>setPage(totalPages)} disabled={page===totalPages}
                                    style={{width:30,height:30,borderRadius:7,border:'1px solid #e2e8f0',background:page===totalPages?'#f8fafc':'#fff',color:page===totalPages?'#cbd5e1':'#374151',cursor:page===totalPages?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11}}>
                                    <i className="fas fa-angle-double-right" />
                                </button>
                            </div>
                        </div>
                        </>
                    );
                })()}

                {/* Delete confirm modal */}
                {deleteConfirm && (
                    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
                        <div style={{background:'#fff',borderRadius:20,maxWidth:420,width:'100%',overflow:'hidden',boxShadow:'0 24px 64px rgba(220,38,38,0.18)'}}>
                            <div style={{background:'linear-gradient(135deg,#fee2e2,#fecaca)',padding:'18px 22px',display:'flex',alignItems:'center',gap:12,borderBottom:'1px solid #fca5a5'}}>
                                <div style={{width:42,height:42,background:'#dc2626',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                    <i className="fas fa-trash" style={{color:'#fff',fontSize:16}} />
                                </div>
                                <div>
                                    <div style={{fontWeight:800,fontSize:15,color:'#991b1b'}}>Delete Assignment</div>
                                    <div style={{fontSize:12,color:'#b91c1c',marginTop:1}}>This action cannot be undone</div>
                                </div>
                            </div>
                            <div style={{padding:'18px 22px'}}>
                                <p style={{fontSize:14,color:'#374151',margin:0}}>
                                    Delete <strong>"{deleteConfirm.title}"</strong>?
                                </p>
                                <div style={{marginTop:10,background:'#fef2f2',border:'1px solid #fecaca',borderRadius:8,padding:'9px 12px',fontSize:12,color:'#b91c1c'}}>
                                    <i className="fas fa-exclamation-triangle" style={{marginRight:6}} />
                                    The assignment will be permanently removed for all students.
                                </div>
                            </div>
                            <div style={{padding:'12px 22px',borderTop:'1px solid #f1f5f9',display:'flex',gap:10,justifyContent:'flex-end',background:'#fafafa'}}>
                                <GhostBtn onClick={()=>setDeleteConfirm(null)}>Cancel</GhostBtn>
                                <button onClick={()=>{onDeleteAssignment(deleteConfirm.id);setDeleteConfirm(null);}}
                                    style={{background:'linear-gradient(135deg,#dc2626,#b91c1c)',color:'#fff',border:'none',borderRadius:10,padding:'9px 20px',fontSize:13,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',gap:7}}>
                                    <i className="fas fa-trash" /> Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* View Assignment Modal — same as Assignments tab */}
            {viewAssignment && (() => {
                const a  = viewAssignment;
                const c  = colorOf(a.subject || 'x');
                const dl = daysLeft(a.dueDate);
                return (
                    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
                        <div style={{background:'#fff',borderRadius:20,maxWidth:720,width:'100%',maxHeight:'95vh',overflow:'hidden',display:'flex',flexDirection:'column',boxShadow:'0 24px 64px rgba(99,102,241,0.18)'}}>
                            <div style={{background:'linear-gradient(135deg,#f0fdf4,#dcfce7)',borderBottom:'1.5px solid #86efac',padding:'20px 26px',display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12}}>
                                <div style={{flex:1}}>
                                    <div style={{color:'#16a34a',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:4}}>Assignment Details</div>
                                    <div style={{color:'#14532d',fontWeight:800,fontSize:18,lineHeight:1.3,marginBottom:8}}>{a.title}</div>
                                    <div style={{display:'flex',gap:7,flexWrap:'wrap',alignItems:'center'}}>
                                        <span style={{background:c,color:'#fff',borderRadius:20,padding:'3px 13px',fontSize:12,fontWeight:700}}>{a.subject}</span>
                                        <span style={{background:a.status==='active'?'#16a34a':'#64748b',color:'#fff',borderRadius:20,padding:'3px 11px',fontSize:12,fontWeight:700,textTransform:'capitalize'}}>{a.status}</span>
                                    </div>
                                </div>
                                <button onClick={()=>setViewAssignment(null)} style={{background:'#fff',border:'1.5px solid #86efac',color:'#16a34a',borderRadius:'50%',width:32,height:32,fontSize:16,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontWeight:700}}>×</button>
                            </div>
                            <div style={{padding:'22px 26px',overflowY:'auto',flex:1}}>
                                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:20}}>
                                    {[
                                        ['fa-star',          'Max Marks', `${a.maxMarks} marks`,    '#8b5cf6','#ede9fe'],
                                        ['fa-play-circle',   'Start Date', fmtDate(a.startDate)||'—','#3b82f6','#dbeafe'],
                                        ['fa-flag-checkered','End Date',   fmtDate(a.dueDate)||'—',  '#ef4444','#fee2e2'],
                                        ['fa-users',         'Students',   `${a.student_count||0}`,  '#10b981','#d1fae5'],
                                        ['fa-code-branch',   'Branch',     a.branch_name||'—',       '#f59e0b','#fef3c7'],
                                        ['fa-graduation-cap','Class',      a.class_name||'—',        '#7c3aed','#ede9fe'],
                                        ['fa-layer-group',   'Section',    a.Section_Name||'—',      '#0891b2','#cffafe'],
                                        ['fa-clock',         'Days Left',  dl===null?'—':dl<0?`${Math.abs(dl)}d overdue`:dl===0?'Due today':`${dl}d left`, dl!==null&&dl<0?'#dc2626':dl===0?'#f59e0b':'#16a34a', dl!==null&&dl<0?'#fee2e2':dl===0?'#fef3c7':'#dcfce7'],
                                    ].map(([ic,lb,val,col,bg])=>(
                                        <div key={lb} style={{background:bg,borderRadius:10,padding:'10px 12px'}}>
                                            <div style={{fontSize:10,fontWeight:700,color:col,textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:4}}><i className={`fas ${ic}`} style={{marginRight:5}} />{lb}</div>
                                            <div style={{fontSize:13,fontWeight:700,color:'#1f2937'}}>{val}</div>
                                        </div>
                                    ))}
                                </div>
                                {a.description && (
                                    <div style={{marginBottom:16}}>
                                        <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:8}}><i className="fas fa-align-left" style={{marginRight:6,color:c}} />Description</div>
                                        <div style={{background:'#f8fafc',borderRadius:10,padding:'14px 16px',fontSize:13,color:'#374151',lineHeight:1.7,border:'1px solid #e2e8f0',whiteSpace:'pre-wrap'}}>{a.description}</div>
                                    </div>
                                )}
                                {a.file_path && (() => {
                                    // Parse optional filename fragment: url#filename.pdf
                                    const hashIdx   = a.file_path.indexOf('#');
                                    const rawUrl    = hashIdx > -1 ? a.file_path.slice(0, hashIdx) : a.file_path;
                                    const fragName  = hashIdx > -1 ? decodeURIComponent(a.file_path.slice(hashIdx + 1)) : '';
                                    const isUrl     = /^https?:\/\//i.test(rawUrl);
                                    // Detect type from fragment name first, then URL itself
                                    const nameForType = fragName || rawUrl;
                                    const isPdf     = /\.pdf$/i.test(nameForType);
                                    const isImg     = /\.(png|jpg|jpeg|gif|webp)$/i.test(nameForType);
                                    // For GCP getFileData URLs without a recognised extension, allow viewing anyway
                                    const isViewable = isPdf || isImg || (isUrl && /getFileData/i.test(rawUrl));
                                    const fileIcon  = isPdf ? 'fa-file-pdf' : isImg ? 'fa-file-image' : 'fa-file-alt';
                                    // fragName = original filename (new uploads); fallback for old records
                                    const fileName  = fragName || (isUrl ? 'Uploaded File' : a.file_path);
                                    const fileLabel = isPdf ? 'PDF Document' : isImg ? 'Image File' : 'Document';
                                    return (
                                        <div style={{marginBottom:16}}>
                                            <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:8}}>
                                                <i className="fas fa-paperclip" style={{marginRight:6,color:c}} />Attached File
                                            </div>
                                            <div style={{display:'flex',alignItems:'center',gap:10,background:'#f8fafc',border:'1.5px solid #e2e8f0',borderRadius:10,padding:'10px 14px',borderBottomLeftRadius:(isViewable&&showPdfPreview)?0:10,borderBottomRightRadius:(isViewable&&showPdfPreview)?0:10}}>
                                                <div style={{width:36,height:36,background:`${c}18`,borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                                    <i className={`fas ${fileIcon}`} style={{fontSize:18,color:c}} />
                                                </div>
                                                <div style={{flex:1,overflow:'hidden'}}>
                                                    <div style={{fontWeight:700,fontSize:13,color:'#1f2937',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{fileName}</div>
                                                    <div style={{fontSize:11,color:'#64748b',marginTop:2}}>{fileLabel}</div>
                                                </div>
                                                <div style={{display:'flex',gap:8,flexShrink:0}}>
                                                    {isViewable && (
                                                        <button onClick={()=>setShowPdfPreview(v=>!v)}
                                                            style={{padding:'6px 12px',borderRadius:8,border:`1.5px solid ${c}`,background:showPdfPreview?c:'#fff',color:showPdfPreview?'#fff':c,fontSize:12,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',gap:5}}>
                                                            <i className={`fas ${showPdfPreview?'fa-eye-slash':'fa-eye'}`} />
                                                            {showPdfPreview ? 'Hide' : 'View File'}
                                                        </button>
                                                    )}
                                                    {isUrl && (
                                                        <a href={rawUrl} target="_blank" rel="noopener noreferrer"
                                                            style={{padding:'6px 12px',borderRadius:8,border:'1.5px solid #e2e8f0',background:'#f1f5f9',color:'#374151',fontSize:12,fontWeight:700,textDecoration:'none',display:'flex',alignItems:'center',gap:5}}>
                                                            <i className="fas fa-download" />Download
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                            {showPdfPreview && isViewable && (
                                                <div style={{border:'1.5px solid #e2e8f0',borderTop:'none',borderRadius:'0 0 10px 10px',overflow:'hidden'}}>
                                                    <div style={{background:'#f8fafc',borderBottom:'1px solid #e2e8f0',padding:'7px 14px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                                                        <span style={{fontSize:12,fontWeight:600,color:'#64748b'}}><i className="fas fa-file-pdf" style={{color:'#ef4444',marginRight:6}} />{fileName}</span>
                                                        <a href={rawUrl} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:PURPLE,fontWeight:700,textDecoration:'none',display:'flex',alignItems:'center',gap:4}}>
                                                            <i className="fas fa-external-link-alt" />Open in new tab
                                                        </a>
                                                    </div>
                                                    {isImg ? (
                                                        <div style={{padding:16,textAlign:'center',background:'#f8fafc'}}>
                                                            <img src={rawUrl} alt={fileName} style={{maxWidth:'100%',maxHeight:480,borderRadius:8,boxShadow:'0 4px 16px rgba(0,0,0,0.1)'}} />
                                                        </div>
                                                    ) : (
                                                        <iframe src={rawUrl} style={{width:'100%',height:520,border:'none',display:'block'}} title={fileName} />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                            <div style={{padding:'14px 26px',borderTop:'1px solid #f1f5f9',display:'flex',gap:10,justifyContent:'flex-end',background:'#fafafa'}}>
                                <GhostBtn onClick={()=>setViewAssignment(null)}>Close</GhostBtn>
                                <PrimaryBtn onClick={()=>{setViewAssignment(null);onEdit(a);}}><i className="fas fa-pen" /> Edit Assignment</PrimaryBtn>
                            </div>
                        </div>
                    </div>
                );
            })()}

        </div>
    );
}

// ─── Teacher: Assignments (Create / Edit only) ────────────────────────────────
function TeacherAssignments({ groups, onSaveAssignment, initialEditing, onClearEditTarget }) {
    const [editing, setEditing] = useState(initialEditing || null);

    useEffect(() => {
        if (initialEditing) {
            setEditing(initialEditing);
            if (onClearEditTarget) onClearEditTarget();
        }
    }, [initialEditing]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <AssignmentForm
            groups={groups}
            editing={editing}
            onSave={() => { onSaveAssignment(); setEditing(null); }}
            onCancel={() => setEditing(null)}
        />
    );
}

// ─── File View Modal ──────────────────────────────────────────────────────────
function FileViewModal({ url, studentName, onClose }) {
    const [loadError, setLoadError] = useState(false);

    if (!url) return null;

    const clean    = url.split('?')[0].split('#')[0].toLowerCase();
    const isPdf    = clean.endsWith('.pdf')  || /getfiledata/i.test(url);
    const isImg    = /\.(png|jpg|jpeg|gif|webp|svg)$/.test(clean);
    const isWord   = /\.(doc|docx)$/.test(clean);
    const isExcel  = /\.(xls|xlsx)$/.test(clean);
    const isPpt    = /\.(ppt|pptx)$/.test(clean);
    const isOffice = isWord || isExcel || isPpt;
    const googleViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`;

    const fileIcon = isPdf ? 'fa-file-pdf' : isImg ? 'fa-file-image' : isWord ? 'fa-file-word' : isExcel ? 'fa-file-excel' : isPpt ? 'fa-file-powerpoint' : 'fa-file-alt';
    const fileType = isPdf ? 'PDF' : isImg ? 'Image' : isWord ? 'Word' : isExcel ? 'Excel' : isPpt ? 'PowerPoint' : 'File';
    const typeColor = isPdf ? '#ef4444' : isImg ? '#8b5cf6' : isWord ? '#2563eb' : isExcel ? '#16a34a' : isPpt ? '#ea580c' : '#64748b';

    return (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.65)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
            <div style={{background:'#fff',borderRadius:20,width:'100%',maxWidth:900,maxHeight:'95vh',display:'flex',flexDirection:'column',boxShadow:'0 32px 80px rgba(0,0,0,0.3)',overflow:'hidden'}}>

                {/* Header */}
                <div style={{background:'linear-gradient(135deg,#1e293b,#334155)',padding:'14px 20px',display:'flex',alignItems:'center',gap:12,flexShrink:0}}>
                    <div style={{width:38,height:38,background:`${typeColor}22`,border:`1.5px solid ${typeColor}44`,borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                        <i className={`fas ${fileIcon}`} style={{color:typeColor,fontSize:16}} />
                    </div>
                    <div style={{flex:1,overflow:'hidden'}}>
                        <div style={{color:'#fff',fontWeight:700,fontSize:14,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                            {studentName ? `${studentName}'s Submission` : 'Submitted File'}
                        </div>
                        <span style={{background:typeColor,color:'#fff',borderRadius:20,padding:'1px 9px',fontSize:10,fontWeight:700}}>{fileType}</span>
                    </div>
                    <a href={url} target="_blank" rel="noopener noreferrer"
                        style={{display:'inline-flex',alignItems:'center',gap:6,background:'rgba(255,255,255,0.12)',color:'#fff',borderRadius:8,padding:'6px 13px',fontSize:12,fontWeight:700,textDecoration:'none',flexShrink:0,border:'1px solid rgba(255,255,255,0.18)'}}>
                        <i className="fas fa-download" /> Download
                    </a>
                    <button onClick={onClose}
                        style={{width:32,height:32,borderRadius:'50%',border:'none',background:'rgba(255,255,255,0.12)',color:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>
                        ×
                    </button>
                </div>

                {/* Body */}
                <div style={{flex:1,overflow:'hidden',background:'#f1f5f9',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:0}}>
                    {loadError ? (
                        <div style={{textAlign:'center',padding:40}}>
                            <i className="fas fa-exclamation-triangle" style={{fontSize:40,color:'#f59e0b',display:'block',marginBottom:16}} />
                            <div style={{fontSize:15,fontWeight:700,color:'#374151',marginBottom:8}}>Unable to preview this file</div>
                            <div style={{fontSize:13,color:'#64748b',marginBottom:20}}>The file format may not support inline preview.</div>
                            <a href={url} target="_blank" rel="noopener noreferrer"
                                style={{display:'inline-flex',alignItems:'center',gap:8,background:`linear-gradient(135deg,${PURPLE},#8b5cf6)`,color:'#fff',borderRadius:10,padding:'10px 22px',fontSize:13,fontWeight:700,textDecoration:'none'}}>
                                <i className="fas fa-external-link-alt" /> Open in New Tab
                            </a>
                        </div>
                    ) : isImg ? (
                        <div style={{padding:16,width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',overflow:'auto'}}>
                            <img src={url} alt="Student submission"
                                onError={() => setLoadError(true)}
                                style={{maxWidth:'100%',maxHeight:'calc(95vh - 80px)',objectFit:'contain',borderRadius:8,boxShadow:'0 4px 20px rgba(0,0,0,0.15)'}} />
                        </div>
                    ) : isPdf ? (
                        <iframe src={url} title="PDF Preview"
                            onError={() => setLoadError(true)}
                            style={{width:'100%',height:'calc(95vh - 80px)',border:'none',display:'block'}} />
                    ) : isOffice ? (
                        <iframe src={googleViewerUrl} title="Document Preview"
                            onError={() => setLoadError(true)}
                            style={{width:'100%',height:'calc(95vh - 80px)',border:'none',display:'block'}} />
                    ) : (
                        <div style={{textAlign:'center',padding:40}}>
                            <i className={`fas ${fileIcon}`} style={{fontSize:52,color:typeColor,display:'block',marginBottom:16}} />
                            <div style={{fontSize:15,fontWeight:700,color:'#374151',marginBottom:8}}>Preview not available</div>
                            <div style={{fontSize:13,color:'#64748b',marginBottom:20}}>This file type cannot be previewed in the browser.</div>
                            <a href={url} target="_blank" rel="noopener noreferrer"
                                style={{display:'inline-flex',alignItems:'center',gap:8,background:`linear-gradient(135deg,${PURPLE},#8b5cf6)`,color:'#fff',borderRadius:10,padding:'10px 22px',fontSize:13,fontWeight:700,textDecoration:'none'}}>
                                <i className="fas fa-download" /> Download File
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Teacher: Grading Inbox ───────────────────────────────────────────────────
// `guard` is the shared useModuleGuard instance from AssignmentHome — see AssignmentForm.
function GradingInbox({ assignments, initialAssignment, loadingAssignments, fromDate, toDate, setFromDate, setToDate, guard }) {
    const client = useApolloClient();
    const [selectedId, setSelectedId] = useState(initialAssignment?.id ? Number(initialAssignment.id) : null);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading]     = useState(false);
    const [fetchError, setFetchError] = useState('');
    const [panel, setPanel]         = useState(null);
    const [fileModal, setFileModal] = useState(null); // { url, studentName }
    const [showPanelFile, setShowPanelFile] = useState(false);
    const [gradeVal, setGradeVal]   = useState('');
    const [feedback, setFeedback]   = useState('');
    const [toast, setToast]         = useState('');
    const [saving, setSaving]       = useState(false);

    // Submission Review filters — multi-select (arrays of selected names).
    // fBranch is NOT user-picked: it mirrors the header's (NavbarOne) branch scope.
    const [fBranch,  setFBranch]  = useState([]);
    const [fClass,   setFClass]   = useState([]);
    const [fStream,  setFStream]  = useState([]);
    const [fSection, setFSection] = useState([]);
    const [fSubject, setFSubject] = useState([]);
    const [allStreams, setAllStreams] = useState([]);

    useEffect(() => {
        client.query({ query: GET_STREAMS, fetchPolicy: 'network-only' })
            .then(res => { setAllStreams(res.data?.getStreams || []); })
            .catch(() => {});
    }, []);

    // These rows carry branch_name, not branch_id, so the header's scope is resolved
    // to names via getBranches. [] = unrestricted (every branch the user may see).
    useEffect(() => {
        const applyScope = () => {
            client.query({
                query: GET_BRANCHES,
                variables: { institution_id: parseInt(Cookies.get('institutionid')) },
                fetchPolicy: 'cache-first',
            })
                .then(res => {
                    setFBranch(scopeBranches(res.data?.getBranches || []).map(b => b.branch_name).filter(Boolean));
                })
                .catch(() => {});
        };
        applyScope();
        return onBranchScopeChange(applyScope);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Submissions table search / status filter / pagination
    const [subSearch,       setSubSearch]       = useState('');
    const [subStatusFilter, setSubStatusFilter] = useState('all'); // all | submitted | graded | pending
    const [subPage,         setSubPage]         = useState(1);
    const [subPageSize,     setSubPageSize]     = useState(10);

    // Sync selection when parent passes a new initialAssignment (e.g. after creating)
    useEffect(() => {
        if (initialAssignment?.id) setSelectedId(Number(initialAssignment.id));
    }, [initialAssignment?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const assignment = assignments.find(a => Number(a.id) === Number(selectedId));

    // ── Filter options (cascade: each list is narrowed by the selections above it) ──
    const toVals = arr => [...new Set(arr.filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));
    const mkOpts = vals => vals.map(v => ({ value: v, label: v }));

    const classVals   = toVals(assignments
        .filter(a => !fBranch.length || fBranch.includes(a.branch_name))
        .map(a => a.class_name));
    const streamVals  = toVals(assignments
        .filter(a => (!fBranch.length || fBranch.includes(a.branch_name)) && (!fClass.length || fClass.includes(a.class_name)))
        .map(a => {
            if (a.stream_name) return a.stream_name;
            if (a.stream) return a.stream;
            if (a.stream_id) {
                const found = allStreams.find(st => Number(st.id) === Number(a.stream_id));
                return found ? found.stream : `Stream ${a.stream_id}`;
            }
            return null;
        }));
    const sectionVals = toVals(assignments
        .filter(a => (!fBranch.length || fBranch.includes(a.branch_name)) && (!fClass.length || fClass.includes(a.class_name)) && (!fStream.length || !a.stream_name || fStream.includes(a.stream_name)))
        .map(a => a.Section_Name));
    const subjectVals = toVals(assignments
        .filter(a => (!fBranch.length || fBranch.includes(a.branch_name)) && (!fClass.length || fClass.includes(a.class_name)) && (!fStream.length || !a.stream_name || fStream.includes(a.stream_name)) && (!fSection.length || fSection.includes(a.Section_Name)))
        .map(a => a.subject));

    const classOptions   = mkOpts(classVals);
    const streamOptions  = streamVals.length > 0 ? mkOpts(streamVals) : allStreams.map(st => ({ value: st.stream, label: st.stream }));
    const sectionOptions = mkOpts(sectionVals);
    const subjectOptions = mkOpts(subjectVals);

    useEffect(() => {
        if (streamOptions.length > 0 && fStream.length === 0) {
            setFStream(streamOptions.map(o => o.value));
        }
    }, [streamOptions.map(o => o.value).join(',')]);

    // Show only assignments whose created date falls within the selected range
    const inDateRange = a => {
        if (!a.created) return true;
        if (fromDate && a.created < fromDate) return false;
        if (toDate   && a.created > toDate)   return false;
        return true;
    };

    const filteredAssignments = assignments.filter(a => {
        if (!inDateRange(a)) return false;
        if (fBranch.length && !fBranch.includes(a.branch_name)) return false;
        if (fClass.length  && !fClass.includes(a.class_name)) return false;
        if (fStream.length) {
            const stName = a.stream_name || a.stream || (a.stream_id ? (allStreams.find(st => Number(st.id) === Number(a.stream_id))?.stream) : null);
            if (stName && !fStream.includes(stName)) return false;
        }
        if (fSection.length && !fSection.includes(a.Section_Name)) return false;
        if (fSubject.length && !fSubject.includes(a.subject)) return false;
        return true;
    });

    // All filters start with every value selected ("select all" by default).
    // fBranch is deliberately NOT reset here — it mirrors the header's branch scope,
    // and re-selecting every branch_name would widen past it.
    const selectAll = () => {
        setFClass(toVals(assignments.map(a => a.class_name)));
        setFSection(toVals(assignments.map(a => a.Section_Name)));
        setFSubject(toVals(assignments.map(a => a.subject)));
    };
    const allSelected = classOptions.length > 0 &&
        fClass.length === classOptions.length &&
        fSection.length === sectionOptions.length && fSubject.length === subjectOptions.length;
    const hasFilters = assignments.length > 0 && !allSelected;
    const clearFilters = selectAll;

    // Default to all values selected once assignments arrive
    const initedRef = useRef(false);
    useEffect(() => {
        if (!initedRef.current && assignments.length > 0) {
            initedRef.current = true;
            selectAll();
        }
    }, [assignments.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // When a parent filter changes (select / unselect), refresh the dependent dropdowns:
    // re-select every value still valid in the new scope so the child fields stay in sync.
    const valsFor = (key, predicate) => toVals(assignments.filter(predicate).map(a => a[key]));
    // No onChangeBranch — Branch is not a dropdown here any more.
    const onChangeClass = v => {
        setFClass(v);
        const inScope = a => (!fBranch.length || fBranch.includes(a.branch_name)) && (!v.length || v.includes(a.class_name));
        setFStream([]);
        setFSection(valsFor('Section_Name', inScope));
        setFSubject(valsFor('subject', inScope));
    };
    const onChangeStream = v => {
        setFStream(v);
        const inScope = a => (!fBranch.length || fBranch.includes(a.branch_name)) && (!fClass.length || fClass.includes(a.class_name)) && (!v.length || v.includes(a.stream_name || a.stream));
        setFSection(valsFor('Section_Name', inScope));
        setFSubject(valsFor('subject', inScope));
    };
    const onChangeSection = v => {
        setFSection(v);
        const inScope = a => (!fBranch.length || fBranch.includes(a.branch_name)) && (!fClass.length || fClass.includes(a.class_name)) && (!v.length || v.includes(a.Section_Name));
        setFSubject(valsFor('subject', inScope));
    };
    const onChangeSubject = setFSubject;

    // Clear the selected assignment if it no longer matches the active filters / date range
    useEffect(() => {
        if (selectedId && !filteredAssignments.some(a => Number(a.id) === Number(selectedId))) {
            setSelectedId(null);
        }
    }, [fBranch, fClass, fSection, fSubject, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchSubmissions = (id) => {
        if (!id) { setSubmissions([]); return; }
        setLoading(true);
        setFetchError('');
        client.query({
            query: GET_ASSIGNMENT_SUBMISSIONS,
            variables: { assignment_id: Number(id) },
            fetchPolicy: 'network-only',
        }).then(res => {
            setSubmissions(res.data?.getAssignmentSubmissions || []);
            setLoading(false);
        }).catch(err => {
            console.error('getAssignmentSubmissions error:', err);
            setFetchError(err?.message || 'Failed to load submissions.');
            setLoading(false);
        });
    };

    useEffect(() => { fetchSubmissions(selectedId); }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-select first assignment once the list loads (if nothing is selected yet)
    useEffect(() => {
        if (!selectedId && assignments.length > 0) {
            setSelectedId(Number(assignments[0].id));
        }
    }, [assignments.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // status: 0 = pending (not submitted), 1 = submitted (awaiting grading), 2 = graded
    const subStatusLabel = s => s.status === 0 ? 'pending' : s.status === 2 ? 'graded' : 'submitted';
    const submittedCount = submissions.filter(s => s.status === 1).length; // submitted but not yet graded (moves to Graded once graded)
    const pendingCount   = submissions.filter(s => s.status === 0).length;
    const gradedCount    = submissions.filter(s => s.status === 2).length;
    const awaitingGrade  = submissions.filter(s => s.status === 1).length; // submitted but not yet graded (for warning banner)

    // Search + status filter (status: 0 = pending, 1 = submitted, 2 = graded)
    const subCountFor = f => f === 'all' ? submissions.length
        : f === 'pending'   ? submissions.filter(s => s.status === 0).length
        : f === 'submitted' ? submissions.filter(s => s.status === 1).length
        : submissions.filter(s => s.status === 2).length;
    const subQuery = subSearch.trim().toLowerCase();
    const filteredSubmissions = submissions.filter(s => {
        const statusOk = subStatusFilter === 'all'
            || (subStatusFilter === 'pending'   && s.status === 0)
            || (subStatusFilter === 'submitted' && s.status === 1)
            || (subStatusFilter === 'graded'    && s.status === 2);
        if (!statusOk) return false;
        if (!subQuery) return true;
        return (s.student_name || '').toLowerCase().includes(subQuery)
            || String(s.student_roll_no || '').toLowerCase().includes(subQuery);
    });

    // Pagination (mirrors the Dashboard table)
    const SUB_PAGE_SIZE    = subPageSize;
    const subTotalPages    = Math.max(1, Math.ceil(filteredSubmissions.length / SUB_PAGE_SIZE));
    const pagedSubmissions = filteredSubmissions.slice((subPage - 1) * SUB_PAGE_SIZE, subPage * SUB_PAGE_SIZE);
    // Reset to page 1 when the assignment, search, or status filter changes
    useEffect(() => { setSubPage(1); }, [selectedId, submissions.length, subSearch, subStatusFilter]);

    const openPanel = sub => {
        setPanel(sub);
        setShowPanelFile(false);
        setGradeVal(sub.marks != null ? String(sub.marks) : '');
        setFeedback(sub.verifier_comments || '');
        setToast('');
    };

    // Grading writes marks back to the submission → is_edit. The grading screen itself
    // stays readable (browsing submissions is a read); only the save is gated. Checked
    // ahead of the marks validation so a denied role isn't asked to fix the input first.
    const saveGrade = () => {
        if (!guard.requireEdit()) return;

        if (!gradeVal) return setToast('Enter marks first.');
        const g = parseInt(gradeVal, 10);
        if (isNaN(g) || g < 0 || g > (assignment?.maxMarks || 500)) return setToast(`Marks must be 0–${assignment?.maxMarks || 500}.`);
        setSaving(true);
        client.mutate({
            mutation: UPDATE_ASSIGNMENT_SUBMISSION,
            variables: { id: panel.id, marks: g, verifier_comments: feedback, verified_by: null },
        }).then(() => {
            setSubmissions(prev => prev.map(s => s.id === panel.id
                ? { ...s, marks: g, verifier_comments: feedback, status: 2 }
                : s
            ));
            setSaving(false);
            setPanel(null);
        }).catch(() => { setSaving(false); setToast('Failed to save grade. Please try again.'); });
    };

    return (
        <div>
            {/* Filters — dashboard style (Branch / Class / Section / Subject, multi-select) */}
            <div style={{background:'#faf8ff',border:'1.5px solid #ede9fe',borderRadius:14,padding:'12px 18px 16px',marginBottom:16}}>
                {/* Title row — Filters (left) · Date Range (right) */}
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10,gap:12,flexWrap:'wrap'}}>
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                        <div style={{width:28,height:28,background:`${PURPLE}18`,borderRadius:7,display:'flex',alignItems:'center',justifyContent:'center'}}>
                            <i className="fas fa-sliders-h" style={{color:PURPLE,fontSize:12}} />
                        </div>
                        <span style={{fontSize:12,fontWeight:800,color:PURPLE,letterSpacing:'0.3px'}}>Filters</span>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                        {hasFilters && (
                            <button onClick={clearFilters}
                                style={{display:'flex',alignItems:'center',gap:6,background:`${PURPLE}10`,border:'none',borderRadius:8,padding:'5px 12px',fontSize:12,fontWeight:700,color:PURPLE,cursor:'pointer'}}>
                                <i className="fas fa-times" />Clear
                            </button>
                        )}
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                            <span style={{fontSize:10,fontWeight:700,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.5px',whiteSpace:'nowrap'}}>Date Range</span>
                            <style>{`.asgn-gr-daterange,.asgn-gr-daterange .customDaterange,.asgn-gr-daterange #DateTimeRangeContainerNoMobileMode{width:100%!important}`}</style>
                            <div className="asgn-gr-daterange" style={{minWidth:230}}>
                                <DateBlock
                                    applyCallback={(s, e) => { setFromDate(s.format('YYYY-MM-DD')); setToDate(e.format('YYYY-MM-DD')); }}
                                    initialStart={fromDate}
                                    initialEnd={toDate}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filter controls row — Branch · Class · Stream · Section · Subject (multi) · Assignment (single) */}
                <div style={{display:'flex',alignItems:'flex-end',gap:10,flexWrap:'wrap'}}>
                    {[
                        // No Branch row — the header's branch selection scopes this list.
                        ['Class',   classOptions,   fClass,   onChangeClass,   'All Classes'],
                        ...(streamOptions.length > 0 || fClass.some(c => c.includes('11') || c.includes('12') || c.includes('XI') || c.includes('XII')) ? [['Stream', streamOptions, fStream, onChangeStream, 'All Streams']] : []),
                        ['Section', sectionOptions, fSection, onChangeSection, 'All Sections'],
                        ['Subject', subjectOptions, fSubject, onChangeSubject, 'All Subjects'],
                    ].map(([label, opts, val, onChange, placeholder], idx) => (
                        <React.Fragment key={label}>
                            {idx > 0 && <div style={{width:1,height:36,background:'#e0d9ff',flexShrink:0}} />}
                            <div style={{display:'flex',flexDirection:'column',gap:3,minWidth:160,maxWidth:200,flex:'1 1 160px'}}>
                                <label style={{fontSize:10,fontWeight:700,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.5px'}}>{label}</label>
                                <CheckboxDropdown
                                    options={opts}
                                    value={val}
                                    onChange={onChange}
                                    placeholder={loadingAssignments ? 'Loading…' : placeholder}
                                    disabled={loadingAssignments || assignments.length === 0}
                                />
                            </div>
                        </React.Fragment>
                    ))}

                    <div style={{width:1,height:36,background:'#e0d9ff',flexShrink:0}} />

                    {/* Assignment — single select, aligned beside Subject */}
                    <div style={{display:'flex',flexDirection:'column',gap:3,minWidth:200,maxWidth:280,flex:'1 1 220px'}}>
                        <label style={{fontSize:10,fontWeight:700,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.5px'}}>Assignment</label>
                        <div style={{display:'flex',gap:8,alignItems:'center'}}>
                            <select value={selectedId||''}
                                onChange={e => { const v = e.target.value; setSelectedId(v ? Number(v) : null); }}
                                disabled={loadingAssignments || filteredAssignments.length === 0}
                                style={{flex:1,minWidth:0,borderRadius:10,border:'1.5px solid #e5e7eb',padding:'9px 14px',fontSize:14,color:selectedId?'#1f2937':'#9ca3af',background:'#f9fafb',outline:'none',cursor:'pointer',opacity:(loadingAssignments||filteredAssignments.length===0)?0.7:1}}>
                                <option value="">
                                    {loadingAssignments ? 'Loading…' : assignments.length === 0 ? 'No assignments found' : filteredAssignments.length === 0 ? 'No match' : '-- Choose assignment --'}
                                </option>
                                {filteredAssignments.map(a=>(
                                    <option key={a.id} value={a.id}>{a.title} ({a.subject})</option>
                                ))}
                            </select>
                            {selectedId && (
                                <button onClick={() => fetchSubmissions(selectedId)} disabled={loading} title="Reload"
                                    style={{padding:'9px 12px',borderRadius:10,border:`1.5px solid ${PURPLE}`,background:'#fff',color:PURPLE,fontSize:12,fontWeight:700,cursor:loading?'not-allowed':'pointer',whiteSpace:'nowrap',flexShrink:0,opacity:loading?.6:1}}>
                                    <i className={`fas ${loading?'fa-spinner fa-spin':'fa-sync-alt'}`} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {fetchError && (
                <div style={{background:'#fee2e2',border:'1.5px solid #fca5a5',borderRadius:10,padding:'10px 16px',marginBottom:16,fontSize:13,color:'#dc2626',fontWeight:600}}>
                    <i className="fas fa-exclamation-circle" style={{marginRight:7}} />{fetchError}
                </div>
            )}

            {(assignment || selectedId) ? (
                <>
                    {/* Stats */}
                    <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
                        {[['fa-paper-plane','Submitted',submittedCount,'#3b82f6','#dbeafe'],['fa-clock','Pending',pendingCount,'#f59e0b','#fef3c7'],['fa-check-circle','Graded',gradedCount,'#10b981','#d1fae5'],['fa-star','Max Marks',assignment?.maxMarks ?? '—',PURPLE,'#ede9fe']].map(([ic,lb,v,c,bg])=>(
                            <div key={lb} style={{background:'#fff',borderRadius:12,padding:'12px 14px',display:'flex',alignItems:'center',gap:10,border:`1px solid ${bg}`,boxShadow:'0 2px 6px rgba(0,0,0,0.04)'}}>
                                <div style={{width:36,height:36,background:bg,borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                    <i className={`fas ${ic}`} style={{color:c,fontSize:14}} />
                                </div>
                                <div>
                                    {loading
                                        ? <div style={{marginBottom:4}}><Shimmer w={32} h={18} /></div>
                                        : <div style={{fontSize:20,fontWeight:800,color:c,lineHeight:1}}>{v}</div>}
                                    <div style={{fontSize:10,fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.4px'}}>{lb}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Submissions table */}
                    <div style={{background:'#fff',borderRadius:16,border:'1px solid #e2e8f0',overflow:'hidden'}}>
                        {/* Search + status filter bar */}
                        {!loading && submissions.length > 0 && (
                        <div style={{padding:'12px 18px',borderBottom:'1px solid #f1f5f9',display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                            {/* Search */}
                            <div style={{position:'relative',flex:1,minWidth:200,maxWidth:340}}>
                                <i className="fas fa-search" style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'#9ca3af',fontSize:12}} />
                                <input
                                    value={subSearch}
                                    onChange={e=>setSubSearch(e.target.value)}
                                    placeholder="Search by student or roll no…"
                                    style={{width:'100%',padding:'8px 12px 8px 34px',border:'1.5px solid #e2e8f0',borderRadius:10,fontSize:13,outline:'none',background:'#f9fafb',color:'#1f2937'}}
                                />
                            </div>
                            {/* Status filter tabs */}
                            <div style={{display:'flex',background:'#f1f5f9',borderRadius:10,padding:4,gap:2,border:'1px solid #e2e8f0',flexWrap:'wrap'}}>
                                {['all','submitted','graded','pending'].map(f=>(
                                    <button key={f} onClick={()=>setSubStatusFilter(f)}
                                        style={{padding:'6px 14px',borderRadius:8,border:'none',fontSize:12,fontWeight:700,cursor:'pointer',background:subStatusFilter===f?PURPLE:'transparent',color:subStatusFilter===f?'#fff':'#64748b',textTransform:'capitalize',display:'flex',alignItems:'center',gap:5,transition:'all 0.15s'}}>
                                        {f}
                                        <span style={{background:subStatusFilter===f?'rgba(255,255,255,0.25)':'#e2e8f0',color:subStatusFilter===f?'#fff':'#64748b',borderRadius:20,padding:'1px 7px',fontSize:10,fontWeight:800,minWidth:18,textAlign:'center'}}>
                                            {subCountFor(f)}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        )}
                        {loading ? (
                            <ShimmerTable widths={[30,140,70,120,120,60,90,80,90,120,90,80,90]} rows={7} />
                        ) : (
                            <div style={{overflowX:'auto'}}>
                            <table style={{width:'100%',borderCollapse:'collapse',minWidth:1100}}>
                                <thead>
                                    <tr style={{background:'linear-gradient(135deg,#f5f3ff,#f0f9ff)',borderBottom:'2px solid #e2e8f0'}}>
                                        {['#','Student','Roll No','Comments','Upload','Marks','Submitted On','Verified By','Verified On','Verifier Comments','Timestamp','Status','Action'].map((h,i)=>(
                                            <th key={h} style={{padding:'10px 12px',fontSize:11,fontWeight:700,color:'#475569',textTransform:'uppercase',letterSpacing:'0.4px',textAlign:i===0||h==='Action'?'center':'left',border:'none',whiteSpace:'nowrap',...(h==='Action'?{position:'sticky',right:0,zIndex:3,background:'#f3f0ff',boxShadow:'-4px 0 6px -4px rgba(0,0,0,0.12)'}:{})}}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedSubmissions.map((sub,i) => (
                                        <tr key={sub.id} style={{borderBottom:'1px solid #f3f0ff',background:i%2===0?'#fff':'#faf8ff'}}>
                                            <td style={{padding:'10px 12px',textAlign:'center',fontSize:12,color:'#64748b'}}>{(subPage-1)*SUB_PAGE_SIZE+i+1}</td>
                                            <td style={{padding:'10px 12px'}}>
                                                <div style={{display:'flex',alignItems:'center',gap:8}}>
                                                    <div style={{width:30,height:30,background:`${PURPLE}20`,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,color:PURPLE,fontSize:11,flexShrink:0}}>
                                                        {(sub.student_name||'?').charAt(0).toUpperCase()}
                                                    </div>
                                                    <span style={{fontWeight:700,fontSize:13,color:'#1f2937',whiteSpace:'nowrap'}}>{sub.student_name || '—'}</span>
                                                </div>
                                            </td>
                                            <td style={{padding:'10px 12px',fontSize:12,color:'#374151',fontWeight:600,whiteSpace:'nowrap'}}>{sub.student_roll_no || '—'}</td>
                                            <td style={{padding:'10px 12px',fontSize:12,color:'#374151',maxWidth:180}}>
                                                <div style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:160}} title={sub.comments||''}>{sub.comments || <span style={{color:'#94a3b8',fontStyle:'italic'}}>—</span>}</div>
                                            </td>
                                            <td style={{padding:'10px 12px'}}>
                                                {sub.upload_file_path ? (() => {
                                                    const fileName = sub.upload_file_path.split('/').pop().split('?')[0].split('#')[0] || 'file';
                                                    const ext = fileName.split('.').pop().toLowerCase();
                                                    const icon = ext === 'pdf' ? 'fa-file-pdf' : /^(png|jpg|jpeg|gif|webp|svg)$/.test(ext) ? 'fa-file-image' : /^(doc|docx)$/.test(ext) ? 'fa-file-word' : /^(xls|xlsx)$/.test(ext) ? 'fa-file-excel' : /^(ppt|pptx)$/.test(ext) ? 'fa-file-powerpoint' : 'fa-file-alt';
                                                    const color = ext === 'pdf' ? '#ef4444' : /^(png|jpg|jpeg|gif|webp)$/.test(ext) ? '#8b5cf6' : /^(doc|docx)$/.test(ext) ? '#2563eb' : /^(xls|xlsx)$/.test(ext) ? '#16a34a' : '#64748b';
                                                    return (
                                                        <div style={{display:'flex',alignItems:'center',gap:6,maxWidth:160}}>
                                                            <i className={`fas ${icon}`} style={{color,fontSize:14,flexShrink:0}} />
                                                            <span style={{fontSize:12,color:'#374151',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={fileName}>{fileName}</span>
                                                        </div>
                                                    );
                                                })() : <span style={{color:'#94a3b8',fontSize:12}}>—</span>}
                                            </td>
                                            <td style={{padding:'10px 12px',fontSize:13,fontWeight:700,color:sub.marks!=null?'#16a34a':'#94a3b8',whiteSpace:'nowrap'}}>
                                                {sub.marks != null ? `${sub.marks}/${assignment.maxMarks}` : '—'}
                                            </td>
                                            <td style={{padding:'10px 12px',fontSize:12,color:'#64748b',whiteSpace:'nowrap'}}>{sub.submitted_time ? fmtDT(new Date(Number(sub.submitted_time)*1000).toISOString()) : '—'}</td>
                                            <td style={{padding:'10px 12px',fontSize:12,color:'#64748b',textAlign:'center'}}>{sub.verified_by || '—'}</td>
                                            <td style={{padding:'10px 12px',fontSize:12,color:'#64748b',whiteSpace:'nowrap'}}>{sub.verified_time ? fmtDT(new Date(Number(sub.verified_time)*1000).toISOString()) : '—'}</td>
                                            <td style={{padding:'10px 12px',fontSize:12,color:'#374151',maxWidth:160}}>
                                                <div style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:140}} title={sub.verifier_comments||''}>{sub.verifier_comments || <span style={{color:'#94a3b8',fontStyle:'italic'}}>—</span>}</div>
                                            </td>
                                            <td style={{padding:'10px 12px',fontSize:12,color:'#64748b',whiteSpace:'nowrap'}}>{sub.timestamp ? fmtDT(new Date(Number(sub.timestamp)*1000).toISOString()) : '—'}</td>
                                            <td style={{padding:'10px 12px'}}><Badge status={subStatusLabel(sub)} /></td>
                                            <td style={{padding:'10px 12px',textAlign:'center',position:'sticky',right:0,zIndex:1,background:i%2===0?'#fff':'#faf8ff',boxShadow:'-4px 0 6px -4px rgba(0,0,0,0.12)'}}>
                                                {sub.status === 0 ? (
                                                    <span style={{padding:'5px 12px',borderRadius:8,background:'#f1f5f9',color:'#94a3b8',fontSize:12,fontWeight:600,display:'inline-flex',alignItems:'center',gap:5}}>
                                                        <i className="fas fa-clock" /> Pending
                                                    </span>
                                                ) : sub.status === 2 ? (
                                                    <button
                                                        onClick={()=>openPanel(sub)}
                                                        style={{padding:'6px 14px',borderRadius:8,border:'none',background:`linear-gradient(135deg,${PURPLE},#8b5cf6)`,color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap',display:'inline-flex',alignItems:'center',gap:5}}>
                                                        <i className="fas fa-eye" /> View
                                                    </button>
                                                ) : (
                                                    <button onClick={()=>openPanel(sub)}
                                                        style={{padding:'6px 14px',borderRadius:8,border:'none',background:`linear-gradient(135deg,${PURPLE},#8b5cf6)`,color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap',display:'inline-flex',alignItems:'center',gap:5}}>
                                                        <i className="fas fa-star" /> Grade
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        )}
                        {!loading && submissions.length === 0 && (
                            <div style={{textAlign:'center',padding:'48px',color:'#94a3b8'}}>
                                <i className="fas fa-inbox" style={{fontSize:32,display:'block',marginBottom:10}} />
                                <div style={{fontSize:14,fontWeight:700,color:'#374151'}}>No submissions yet for this assignment</div>
                            </div>
                        )}
                        {!loading && submissions.length > 0 && filteredSubmissions.length === 0 && (
                            <div style={{textAlign:'center',padding:'48px',color:'#94a3b8'}}>
                                <i className="fas fa-search" style={{fontSize:30,display:'block',marginBottom:10}} />
                                <div style={{fontSize:14,fontWeight:700,color:'#374151'}}>No submissions match your search or filter</div>
                            </div>
                        )}
                        {/* Pagination */}
                        {!loading && filteredSubmissions.length > 0 && (
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 18px',borderTop:'1px solid #f1f5f9',background:'#fafafa',flexWrap:'wrap',gap:8}}>
                            <div style={{display:'flex',alignItems:'center',gap:8}}>
                                <span style={{fontSize:12,color:'#64748b'}}>
                                    Showing <strong>{filteredSubmissions.length===0?0:(subPage-1)*SUB_PAGE_SIZE+1}</strong>–<strong>{Math.min(subPage*SUB_PAGE_SIZE,filteredSubmissions.length)}</strong> of <strong>{filteredSubmissions.length}</strong> Results
                                </span>
                                <select
                                    value={subPageSize}
                                    onChange={e=>{setSubPageSize(Number(e.target.value));setSubPage(1);}}
                                    style={{padding:'4px 8px',border:'1.5px solid #e2e8f0',borderRadius:7,fontSize:12,color:'#374151',background:'#fff',cursor:'pointer',outline:'none'}}>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={filteredSubmissions.length||9999}>All</option>
                                </select>
                                <span style={{fontSize:12,color:'#94a3b8'}}>per page</span>
                            </div>
                            <div style={{display:'flex',gap:4,alignItems:'center'}}>
                                <button onClick={()=>setSubPage(1)} disabled={subPage===1}
                                    style={{width:30,height:30,borderRadius:7,border:'1px solid #e2e8f0',background:subPage===1?'#f8fafc':'#fff',color:subPage===1?'#cbd5e1':'#374151',cursor:subPage===1?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11}}>
                                    <i className="fas fa-angle-double-left" />
                                </button>
                                <button onClick={()=>setSubPage(p=>Math.max(1,p-1))} disabled={subPage===1}
                                    style={{width:30,height:30,borderRadius:7,border:'1px solid #e2e8f0',background:subPage===1?'#f8fafc':'#fff',color:subPage===1?'#cbd5e1':'#374151',cursor:subPage===1?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11}}>
                                    <i className="fas fa-angle-left" />
                                </button>
                                {Array.from({length:subTotalPages},(_,k)=>k+1)
                                    .filter(p=>p===1||p===subTotalPages||Math.abs(p-subPage)<=1)
                                    .reduce((acc,p,idx,arr)=>{
                                        if(idx>0&&p-arr[idx-1]>1) acc.push('...');
                                        acc.push(p); return acc;
                                    },[])
                                    .map((p,idx)=> p==='...'
                                        ? <span key={'e'+idx} style={{padding:'0 4px',color:'#94a3b8',fontSize:12}}>…</span>
                                        : <button key={p} onClick={()=>setSubPage(p)}
                                            style={{width:30,height:30,borderRadius:7,border:`1px solid ${subPage===p?PURPLE:'#e2e8f0'}`,background:subPage===p?PURPLE:'#fff',color:subPage===p?'#fff':'#374151',cursor:'pointer',fontWeight:subPage===p?700:400,fontSize:12,display:'flex',alignItems:'center',justifyContent:'center'}}>
                                            {p}
                                          </button>
                                    )
                                }
                                <button onClick={()=>setSubPage(p=>Math.min(subTotalPages,p+1))} disabled={subPage===subTotalPages}
                                    style={{width:30,height:30,borderRadius:7,border:'1px solid #e2e8f0',background:subPage===subTotalPages?'#f8fafc':'#fff',color:subPage===subTotalPages?'#cbd5e1':'#374151',cursor:subPage===subTotalPages?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11}}>
                                    <i className="fas fa-angle-right" />
                                </button>
                                <button onClick={()=>setSubPage(subTotalPages)} disabled={subPage===subTotalPages}
                                    style={{width:30,height:30,borderRadius:7,border:'1px solid #e2e8f0',background:subPage===subTotalPages?'#f8fafc':'#fff',color:subPage===subTotalPages?'#cbd5e1':'#374151',cursor:subPage===subTotalPages?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11}}>
                                    <i className="fas fa-angle-double-right" />
                                </button>
                            </div>
                        </div>
                        )}
                    </div>

                    {awaitingGrade > 0 && (
                        <div style={{marginTop:12,background:'#fef3c7',border:'1.5px solid #fde68a',borderRadius:10,padding:'10px 16px',fontSize:13,color:'#92400e',fontWeight:600}}>
                            <i className="fas fa-exclamation-circle" style={{marginRight:7}} />
                            {awaitingGrade} submission{awaitingGrade>1?'s':''} waiting to be graded.
                        </div>
                    )}
                </>
            ) : (
                <div style={{textAlign:'center',padding:'60px',color:'#94a3b8',background:'#f8fafc',borderRadius:16,border:'2px dashed #e2e8f0'}}>
                    <i className="fas fa-star" style={{fontSize:36,display:'block',marginBottom:12}} />
                    <div style={{fontSize:15,fontWeight:700,color:'#374151'}}>Select an assignment to review submissions</div>
                </div>
            )}

            {/* File view modal */}
            {fileModal && (
                <FileViewModal
                    url={fileModal.url}
                    studentName={fileModal.studentName}
                    onClose={() => setFileModal(null)}
                />
            )}

            {/* Grade / view panel */}
            {panel && (
                <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
                    <div style={{background:'#fff',borderRadius:20,maxWidth:680,width:'100%',maxHeight:'90vh',overflow:'hidden',display:'flex',flexDirection:'column',boxShadow:'0 24px 64px rgba(99,102,241,0.2)'}}>
                        <div style={{background:`linear-gradient(135deg,${PURPLE},#8b5cf6)`,padding:'18px 24px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                            <div>
                                <div style={{color:'#fff',fontWeight:800,fontSize:16}}>{panel.student_name || 'Student'}</div>
                                <div style={{color:'rgba(255,255,255,0.7)',fontSize:12,marginTop:2}}>{panel.student_roll_no} · {assignment?.title}</div>
                            </div>
                            <button onClick={()=>{setPanel(null);setToast('');}} style={{background:'rgba(255,255,255,0.2)',border:'none',color:'#fff',borderRadius:'50%',width:32,height:32,fontSize:18,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
                        </div>
                        <div style={{padding:24,overflowY:'auto',flex:1}}>
                            {toast && <div style={{background:'#fee2e2',border:'1.5px solid #fca5a5',borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:13,color:'#dc2626',fontWeight:700}}>{toast}</div>}

                            {/* Student's Submission */}
                            <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:8}}>Student's Submission</div>

                            {/* Text answer — only show when student typed something */}
                            {panel.comments ? (
                                <div style={{background:'#f8fafc',borderRadius:12,padding:'14px',border:'1px solid #e2e8f0',fontSize:13,color:'#374151',lineHeight:1.7,whiteSpace:'pre-wrap',marginBottom:10}}>
                                    {panel.comments}
                                </div>
                            ) : !panel.upload_file_path ? (
                                <div style={{background:'#f8fafc',borderRadius:12,padding:'14px',border:'1px dashed #e2e8f0',fontSize:13,color:'#94a3b8',fontStyle:'italic',marginBottom:10,textAlign:'center'}}>
                                    No submission yet.
                                </div>
                            ) : null}

                            {/* Uploaded file — View / Hide inline toggle */}
                            {panel.upload_file_path && (() => {
                                const fu = panel.upload_file_path;
                                const cl = fu.split('?')[0].split('#')[0].toLowerCase();
                                const isPdf   = cl.endsWith('.pdf')  || /getfiledata/i.test(fu);
                                const isImg   = /\.(png|jpg|jpeg|gif|webp|svg)$/.test(cl);
                                const isWord  = /\.(doc|docx)$/.test(cl);
                                const isExcel = /\.(xls|xlsx)$/.test(cl);
                                const isPpt   = /\.(ppt|pptx)$/.test(cl);
                                const isTxt   = cl.endsWith('.txt');
                                const isOffice = isWord || isExcel || isPpt;
                                const fIcon   = isPdf ? 'fa-file-pdf' : isImg ? 'fa-file-image' : isWord ? 'fa-file-word' : isExcel ? 'fa-file-excel' : isPpt ? 'fa-file-powerpoint' : isTxt ? 'fa-file-alt' : 'fa-file-alt';
                                const fColor  = isPdf ? '#ef4444' : isImg ? '#8b5cf6' : isWord ? '#2563eb' : isExcel ? '#16a34a' : isPpt ? '#ea580c' : '#64748b';
                                const fileName = fu.split('/').pop().split('?')[0].split('#')[0] || 'Uploaded File';
                                const previewSrc = isOffice
                                    ? `https://docs.google.com/gview?url=${encodeURIComponent(fu)}&embedded=true`
                                    : fu;
                                return (
                                    <div style={{marginBottom:14}}>
                                        {/* File info row */}
                                        <div style={{display:'flex',gap:8,alignItems:'center',background:'#f8fafc',border:'1.5px solid #e2e8f0',borderRadius:showPanelFile ? '10px 10px 0 0' : 10,padding:'10px 14px'}}>
                                            <i className={`fas ${fIcon}`} style={{color:fColor,fontSize:18,flexShrink:0}} />
                                            <span style={{flex:1,fontSize:13,fontWeight:600,color:'#374151',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={fileName}>
                                                {fileName}
                                            </span>
                                            <button
                                                onClick={()=>setShowPanelFile(v=>!v)}
                                                style={{display:'inline-flex',alignItems:'center',gap:6,background:showPanelFile?'#f1f5f9':`linear-gradient(135deg,${PURPLE},#8b5cf6)`,color:showPanelFile?'#374151':'#fff',borderRadius:8,padding:'6px 14px',fontSize:12,fontWeight:700,border:showPanelFile?'1.5px solid #e2e8f0':'none',cursor:'pointer',flexShrink:0}}>
                                                <i className={`fas ${showPanelFile?'fa-eye-slash':'fa-eye'}`} />
                                                {showPanelFile ? 'Hide' : 'View'}
                                            </button>
                                            <a href={fu} target="_blank" rel="noopener noreferrer"
                                                style={{display:'inline-flex',alignItems:'center',background:'#f1f5f9',color:'#374151',borderRadius:8,padding:'6px 10px',fontSize:12,fontWeight:700,textDecoration:'none',flexShrink:0,border:'1.5px solid #e2e8f0'}}>
                                                <i className="fas fa-download" />
                                            </a>
                                        </div>

                                        {/* Inline preview */}
                                        {showPanelFile && (
                                            <div style={{border:'1.5px solid #e2e8f0',borderTop:'none',borderRadius:'0 0 10px 10px',overflow:'hidden',background:'#f1f5f9'}}>
                                                {isImg ? (
                                                    <div style={{padding:16,textAlign:'center',background:'#fff'}}>
                                                        <img src={fu} alt={fileName}
                                                            style={{maxWidth:'100%',maxHeight:400,objectFit:'contain',borderRadius:8,boxShadow:'0 4px 16px rgba(0,0,0,0.1)'}} />
                                                    </div>
                                                ) : (
                                                    <iframe
                                                        src={previewSrc}
                                                        title={fileName}
                                                        style={{width:'100%',height:400,border:'none',display:'block',background:'#fff'}}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                            <div style={{fontSize:12,color:'#64748b',fontWeight:600,marginBottom:16}}>
                                Submitted: {panel.submitted_time ? fmtDT(new Date(Number(panel.submitted_time)*1000).toISOString()) : '—'}
                            </div>

                            {/* Previously graded feedback (view mode) */}
                            {panel.status === 2 && panel.verifier_comments && (
                                <div style={{background:'#fef3c7',border:'1.5px solid #fde68a',borderRadius:10,padding:'10px 14px',marginBottom:16,fontSize:13,color:'#92400e'}}>
                                    <strong>Previous feedback:</strong> {panel.verifier_comments}
                                </div>
                            )}

                            <div style={{height:1,background:'#f1f5f9',marginBottom:16}} />
                            <Row>
                                <Col md={4}>
                                    <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:6}}>Marks (out of {assignment?.maxMarks})</div>
                                    <input type="number" value={gradeVal} onChange={e=>{setGradeVal(e.target.value);setToast('');}} min={0} max={assignment?.maxMarks || 500} placeholder="e.g. 45"
                                        style={{width:'100%',padding:'10px 12px',border:'2px solid #e2e8f0',borderRadius:10,fontSize:16,fontWeight:700,textAlign:'center',outline:'none',color:'#1f2937'}} />
                                </Col>
                                <Col md={8}>
                                    <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:6}}>Feedback / Remarks</div>
                                    <textarea value={feedback} onChange={e=>setFeedback(e.target.value)} rows={3} placeholder="Write feedback for the student…"
                                        style={{width:'100%',padding:'10px 12px',border:'1.5px solid #e2e8f0',borderRadius:10,fontSize:13,resize:'none',outline:'none'}} />
                                </Col>
                            </Row>
                        </div>
                        <div style={{padding:'14px 24px',borderTop:'1px solid #e2e8f0',display:'flex',gap:10,justifyContent:'flex-end',background:'#f8fafc'}}>
                            <GhostBtn onClick={()=>{setPanel(null);setToast('');}}>Cancel</GhostBtn>
                            <PrimaryBtn onClick={saveGrade} disabled={saving} style={{minWidth:150}}>
                                {saving ? <><i className="fas fa-spinner fa-spin" /> Saving…</> : <><i className="fas fa-check" /> Save Grade</>}
                            </PrimaryBtn>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Teacher: Groups Manager ──────────────────────────────────────────────────
function GroupsManager({ groups, students, onAddGroup, onUpdateGroup, onDeleteGroup }) {
    const [newName, setNewName] = useState('');
    const [selGroup, setSelGroup] = useState(null);
    const [toast, setToast] = useState('');

    const createGroup = () => {
        if (!newName.trim()) return setToast('Enter a group name.');
        onAddGroup({ id: genId(), name: newName.trim(), studentIds: [] });
        setNewName('');
        setToast('');
    };

    const toggleMember = (group, studentId) => {
        const inGroup = group.studentIds.includes(studentId);
        const updated = { ...group, studentIds: inGroup ? group.studentIds.filter(id=>id!==studentId) : [...group.studentIds, studentId] };
        onUpdateGroup(updated);
        setSelGroup(updated);
    };

    return (
        <div>
            <SecHead icon="fa-users" title="Group Management" />
            <Row>
                <Col md={5}>
                    <div style={{background:'#f8fafc',borderRadius:12,padding:'14px 16px',marginBottom:16,border:'1px solid #e2e8f0'}}>
                        <div style={{fontSize:12,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:8}}>Create New Group</div>
                        {toast && <div style={{fontSize:12,color:'#dc2626',fontWeight:700,marginBottom:8}}><i className="fas fa-exclamation-circle" style={{marginRight:5}} />{toast}</div>}
                        <div style={{display:'flex',gap:8}}>
                            <input value={newName} onChange={e=>{setNewName(e.target.value);setToast('');}} placeholder="e.g. Class 12 – Section C"
                                style={{flex:1,padding:'9px 12px',border:'1.5px solid #e2e8f0',borderRadius:10,fontSize:13,outline:'none'}}
                                onKeyDown={e=>e.key==='Enter'&&createGroup()} />
                            <PrimaryBtn onClick={createGroup}><i className="fas fa-plus" /></PrimaryBtn>
                        </div>
                    </div>

                    {groups.map(g => (
                        <div key={g.id} className="am-card-hover" onClick={()=>setSelGroup(g.id===selGroup?.id?null:g)}
                            style={{background:selGroup?.id===g.id?`${PURPLE}08`:'#fff',borderRadius:12,border:`2px solid ${selGroup?.id===g.id?PURPLE:'#e2e8f0'}`,padding:'12px 16px',marginBottom:10,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between',gap:10}}>
                            <div>
                                <div style={{fontWeight:700,fontSize:14,color:'#1f2937'}}>{g.name}</div>
                                <div style={{fontSize:12,color:'#64748b',marginTop:3}}>
                                    {g.studentIds.slice(0,3).map(id=>students.find(s=>s.id===id)?.name).filter(Boolean).join(', ')}
                                    {g.studentIds.length>3 && ` +${g.studentIds.length-3} more`}
                                </div>
                            </div>
                            <div style={{display:'flex',alignItems:'center',gap:8}}>
                                <span style={{background:'#ede9fe',color:PURPLE,borderRadius:20,padding:'2px 10px',fontSize:12,fontWeight:700}}>{g.studentIds.length}</span>
                                <button onClick={e=>{e.stopPropagation();onDeleteGroup(g.id);if(selGroup?.id===g.id)setSelGroup(null);}}
                                    style={{background:'#fee2e2',color:'#dc2626',border:'none',borderRadius:8,width:28,height:28,fontSize:12,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                                    <i className="fas fa-trash" />
                                </button>
                            </div>
                        </div>
                    ))}
                </Col>

                <Col md={7}>
                    {selGroup ? (
                        <div style={{background:'#fff',borderRadius:14,border:'1px solid #e2e8f0',padding:'18px 20px'}}>
                            <div style={{fontWeight:800,fontSize:15,color:'#1f2937',marginBottom:14}}>
                                <i className="fas fa-user-friends" style={{color:PURPLE,marginRight:8}} />Members of "{selGroup.name}"
                            </div>
                            <div style={{display:'flex',flexDirection:'column',gap:8}}>
                                {students.map(student => {
                                    const inGroup = selGroup.studentIds.includes(student.id);
                                    return (
                                        <div key={student.id} onClick={()=>toggleMember(selGroup, student.id)}
                                            style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 12px',borderRadius:10,border:`1.5px solid ${inGroup?PURPLE:'#e2e8f0'}`,background:inGroup?`${PURPLE}08`:'#f8fafc',cursor:'pointer'}}>
                                            <div style={{display:'flex',alignItems:'center',gap:10}}>
                                                <div style={{width:32,height:32,background:inGroup?`${PURPLE}20`:'#f1f5f9',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,color:inGroup?PURPLE:'#64748b',fontSize:12}}>{student.name.charAt(0)}</div>
                                                <div>
                                                    <div style={{fontWeight:700,fontSize:13,color:'#1f2937'}}>{student.name}</div>
                                                    <div style={{fontSize:11,color:'#64748b'}}>{student.roll} · {student.email}</div>
                                                </div>
                                            </div>
                                            <span style={{background:inGroup?PURPLE:'#e2e8f0',color:inGroup?'#fff':'#64748b',borderRadius:'50%',width:24,height:24,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,flexShrink:0}}>
                                                <i className={`fas ${inGroup?'fa-check':'fa-plus'}`} />
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div style={{background:'#f8fafc',borderRadius:14,border:'2px dashed #e2e8f0',padding:'48px 24px',textAlign:'center',color:'#94a3b8'}}>
                            <i className="fas fa-mouse-pointer" style={{fontSize:32,display:'block',marginBottom:12}} />
                            <div style={{fontSize:14,fontWeight:700,color:'#374151'}}>Click a group to manage its members</div>
                        </div>
                    )}
                </Col>
            </Row>
        </div>
    );
}

// ─── Teacher: Student Roster ──────────────────────────────────────────────────
function StudentRoster({ students, groups, submissions, onAddStudent, onUpdateStudent, onDeleteStudent }) {
    const [search, setSearch]   = useState('');
    const [form, setForm]       = useState(null);
    const [formData, setFormData] = useState({ name:'', roll:'', email:'' });

    const shown = students.filter(s =>
        !search || s.name.toLowerCase().includes(search.toLowerCase()) ||
        s.roll.toLowerCase().includes(search.toLowerCase()) ||
        (s.email||'').toLowerCase().includes(search.toLowerCase())
    );

    const save = () => {
        if (!formData.name.trim() || !formData.roll.trim()) return;
        if (form?.id) onUpdateStudent({ ...form, ...formData });
        else          onAddStudent({ id: genId(), ...formData });
        setForm(null);
    };     

    return (
        <div>
            <SecHead icon="fa-user-graduate" title={`Student Roster (${students.length})`}
                action={<PrimaryBtn onClick={()=>{setForm({});setFormData({name:'',roll:'',email:''});}}><i className="fas fa-plus" /> Add Student</PrimaryBtn>} />

            {form !== null && (
                <div style={{background:'#f8fafc',borderRadius:12,padding:'16px 18px',marginBottom:18,border:'1.5px solid #e2e8f0'}}>
                    <div style={{fontSize:13,fontWeight:700,color:'#374151',marginBottom:12}}>{form.id?'Edit Student':'Add New Student'}</div>
                    <Row>
                        <Col md={4}>
                            <div style={{marginBottom:12}}>
                                <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:5}}>Full Name *</div>
                                <input value={formData.name} onChange={e=>setFormData(f=>({...f,name:e.target.value}))} placeholder="Arjun Sharma"
                                    style={{width:'100%',padding:'9px 12px',border:'1.5px solid #e2e8f0',borderRadius:10,fontSize:13,outline:'none'}} />
                            </div>
                        </Col>
                        <Col md={4}>
                            <div style={{marginBottom:12}}>
                                <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:5}}>Roll Number *</div>
                                <input value={formData.roll} onChange={e=>setFormData(f=>({...f,roll:e.target.value}))} placeholder="12A01"
                                    style={{width:'100%',padding:'9px 12px',border:'1.5px solid #e2e8f0',borderRadius:10,fontSize:13,outline:'none'}} />
                            </div>
                        </Col>
                        <Col md={4}>
                            <div style={{marginBottom:12}}>
                                <div style={{fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:5}}>Email</div>
                                <input value={formData.email} onChange={e=>setFormData(f=>({...f,email:e.target.value}))} placeholder="arjun@school.in"
                                    style={{width:'100%',padding:'9px 12px',border:'1.5px solid #e2e8f0',borderRadius:10,fontSize:13,outline:'none'}} />
                            </div>
                        </Col>
                    </Row>
                    <div style={{display:'flex',gap:8}}><GhostBtn onClick={()=>setForm(null)}>Cancel</GhostBtn><PrimaryBtn onClick={save}><i className="fas fa-save" /> Save</PrimaryBtn></div>
                </div>
            )}

            <div style={{position:'relative',marginBottom:16}}>
                <i className="fas fa-search" style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'#9ca3af',fontSize:12}} />
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name, roll, or email…"
                    style={{width:'100%',padding:'9px 12px 9px 34px',border:'1.5px solid #e2e8f0',borderRadius:10,fontSize:13,outline:'none'}} />
            </div>

            <div style={{background:'#fff',borderRadius:14,border:'1px solid #e2e8f0',overflow:'hidden'}}>
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                    <thead>
                        <tr style={{background:'linear-gradient(135deg,#f5f3ff,#f0f9ff)',borderBottom:'2px solid #e2e8f0'}}>
                            {['#','Name','Roll No','Email','Groups','Submissions','Actions'].map((h,i)=>(
                                <th key={h} style={{padding:'11px 14px',fontSize:11,fontWeight:700,color:'#475569',textTransform:'uppercase',letterSpacing:'0.5px',textAlign:i===0?'center':'left',border:'none'}}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((student,i) => {
                            const studentGroups = groups.filter(g=>g.studentIds.includes(student.id));
                            const subs = submissions.filter(s=>s.studentId===student.id);
                            return (
                                <tr key={student.id} style={{borderBottom:'1px solid #f3f0ff',background:i%2===0?'#fff':'#faf8ff'}}>
                                    <td style={{padding:'11px 14px',textAlign:'center',fontSize:12,color:'#64748b'}}>{i+1}</td>
                                    <td style={{padding:'11px 14px'}}>
                                        <div style={{display:'flex',alignItems:'center',gap:9}}>
                                            <div style={{width:32,height:32,background:`${PURPLE}20`,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,color:PURPLE,fontSize:12,flexShrink:0}}>{student.name.charAt(0)}</div>
                                            <span style={{fontWeight:700,fontSize:13,color:'#1f2937'}}>{student.name}</span>
                                        </div>
                                    </td>
                                    <td style={{padding:'11px 14px',fontSize:13,fontWeight:600,color:'#374151'}}>{student.roll}</td>
                                    <td style={{padding:'11px 14px',fontSize:12,color:'#64748b'}}>{student.email||'—'}</td>
                                    <td style={{padding:'11px 14px'}}>
                                        <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                                            {studentGroups.map(g=>(
                                                <span key={g.id} style={{background:'#ede9fe',color:PURPLE,borderRadius:20,padding:'2px 9px',fontSize:11,fontWeight:600,whiteSpace:'nowrap'}}>{g.name}</span>
                                            ))}
                                            {!studentGroups.length && <span style={{color:'#94a3b8',fontSize:12}}>None</span>}
                                        </div>
                                    </td>
                                    <td style={{padding:'11px 14px',fontSize:13,color:'#374151',fontWeight:600}}>
                                        {subs.length} sub · {subs.filter(s=>s.status==='graded').length} graded
                                    </td>
                                    <td style={{padding:'11px 14px'}}>
                                        <div style={{display:'flex',gap:6}}>
                                            <button onClick={()=>{setForm(student);setFormData({name:student.name,roll:student.roll,email:student.email||''});}}
                                                style={{width:30,height:30,background:'#f1f5f9',color:'#64748b',border:'none',borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                                                <i className="fas fa-pen" style={{fontSize:11}} />
                                            </button>
                                            <button onClick={()=>onDeleteStudent(student.id)}
                                                style={{width:30,height:30,background:'#fee2e2',color:'#dc2626',border:'none',borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                                                <i className="fas fa-trash" style={{fontSize:11}} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {shown.length === 0 && (
                    <div style={{textAlign:'center',padding:'36px',color:'#94a3b8',fontSize:13}}>
                        <i className="fas fa-user-graduate" style={{fontSize:24,display:'block',marginBottom:8}} />No students found
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Student View ─────────────────────────────────────────────────────────────
function StudentView({ student, assignments, groups, submissions, onAddSubmission, onUpdateSubmission }) {
    const [tab, setTab]   = useState('assignments');
    const [panel, setPanel] = useState(null);
    const [answer, setAnswer] = useState('');
    const [toast, setToast]   = useState('');

    const myGroupIds    = groups.filter(g=>g.studentIds.includes(student.id)).map(g=>g.id);
    const myAssignments = assignments.filter(a=>a.status==='active' && a.groupIds?.some(id=>myGroupIds.includes(id)));
    const mySubmissions = submissions.filter(s=>s.studentId===student.id);

    const getStatus = a => {
        const sub = mySubmissions.find(s=>s.assignmentId===a.id);
        if (sub) return sub.status;
        const dl = daysLeft(a.dueDate); return (dl !== null && dl < 0) ? 'overdue' : 'pending';
    };

    const pending  = myAssignments.filter(a=>!mySubmissions.find(s=>s.assignmentId===a.id));
    const done     = myAssignments.filter(a=> mySubmissions.find(s=>s.assignmentId===a.id));
    const graded   = mySubmissions.filter(s=>s.status==='graded');
    const overdue  = pending.filter(a=>{ const dl=daysLeft(a.dueDate); return dl!==null && dl<0; });

    const openSubmit = a => {
        const existing = mySubmissions.find(s=>s.assignmentId===a.id);
        setPanel(a);
        setAnswer(existing?.answerText || '');
        setToast('');
    };

    const submit = () => {
        if (!answer.trim()) return setToast('Write your answer before submitting.');
        const existing = mySubmissions.find(s=>s.assignmentId===panel.id);
        const dl = daysLeft(panel.dueDate);
        const status = dl < 0 ? 'late' : 'submitted';
        if (existing) {
            onUpdateSubmission({ ...existing, answerText: answer, status, submittedAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
        } else {
            onAddSubmission({ id: genId(), assignmentId: panel.id, studentId: student.id, answerText: answer, submittedAt: new Date().toISOString(), status, grade: null, feedback: '', updatedAt: new Date().toISOString() });
        }
        setPanel(null);
    };

    return (
        <div>
            {/* Profile strip */}
            <div style={{background:`linear-gradient(135deg,${PURPLE}15,#8b5cf615)`,border:`1px solid ${PURPLE}25`,borderRadius:16,padding:'16px 22px',marginBottom:20,display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
                <div style={{width:50,height:50,background:`linear-gradient(135deg,${PURPLE},#8b5cf6)`,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:20,fontWeight:800}}>{student.name.charAt(0)}</div>
                <div style={{flex:1}}>
                    <div style={{fontWeight:800,fontSize:17,color:'#1f2937'}}>{student.name}</div>
                    <div style={{fontSize:13,color:'#64748b'}}>Roll: {student.roll} · {student.email}</div>
                </div>
                {[['fa-clock','Pending',pending.length,'#f59e0b'],['fa-exclamation','Overdue',overdue.length,'#dc2626'],['fa-upload','Submitted',done.length,'#3b82f6'],['fa-star','Graded',graded.length,'#10b981']].map(([ic,lb,v,c])=>(
                    <div key={lb} style={{textAlign:'center',minWidth:56}}>
                        <i className={`fas ${ic}`} style={{color:c,fontSize:14,display:'block',marginBottom:2}} />
                        <div style={{fontWeight:800,fontSize:18,color:c,lineHeight:1}}>{v}</div>
                        <div style={{fontSize:10,fontWeight:700,color:'#64748b',textTransform:'uppercase'}}>{lb}</div>
                    </div>
                ))}
            </div>

            {/* Tabs */}
            <div style={{display:'flex',gap:8,marginBottom:18}}>
                {[['assignments',`Assignments (${myAssignments.length})`,'fa-file-alt'],['history',`History (${done.length})`,'fa-history']].map(([key,label,icon])=>(
                    <button key={key} onClick={()=>setTab(key)}
                        style={{padding:'9px 18px',borderRadius:12,border:'none',fontSize:13,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',gap:7,background:tab===key?PURPLE:'#f1f5f9',color:tab===key?'#fff':'#64748b'}}>
                        <i className={`fas ${icon}`} />{label}
                    </button>
                ))}
            </div>

            {/* Assignments */}
            {tab === 'assignments' && (
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:14}}>
                    {myAssignments.length === 0 && (
                        <div style={{gridColumn:'1/-1',textAlign:'center',padding:'48px',color:'#94a3b8',background:'#f8fafc',borderRadius:14,border:'2px dashed #e2e8f0'}}>
                            <i className="fas fa-file-alt" style={{fontSize:32,display:'block',marginBottom:10}} />
                            <div style={{fontSize:14,fontWeight:700,color:'#374151'}}>No active assignments for your groups</div>
                        </div>
                    )}
                    {[...myAssignments].sort((a, b) => {
                        const subA = mySubmissions.find(s=>s.assignmentId===a.id);
                        const subB = mySubmissions.find(s=>s.assignmentId===b.id);
                        const gradeA = subA?.status==='graded' ? (subA.grade ?? -1) : -Infinity;
                        const gradeB = subB?.status==='graded' ? (subB.grade ?? -1) : -Infinity;
                        return gradeB - gradeA;
                    }).map(a => {
                        const sub    = mySubmissions.find(s=>s.assignmentId===a.id);
                        const status = getStatus(a);
                        const dl     = daysLeft(a.dueDate);
                        const c      = colorOf(a.subject||'x');
                        return (
                            <div key={a.id} className="am-card-hover" style={{background:'#fff',borderRadius:14,border:'1px solid #e2e8f0',overflow:'hidden',boxShadow:'0 2px 10px rgba(0,0,0,0.05)'}}>
                                <div style={{height:5,background:c}} />
                                <div style={{padding:'14px 16px'}}>
                                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
                                        <div style={{fontWeight:800,fontSize:13,color:'#1f2937',flex:1,marginRight:8,lineHeight:1.3}}>{a.title}</div>
                                        <Badge status={status} />
                                    </div>
                                    <div style={{marginBottom:10}}><SubjectChip subject={a.subject} /></div>
                                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:10}}>
                                        {[['fa-book','Chapter',a.chapter||'—'],['fa-star','Marks',`${a.maxMarks} marks`],['fa-calendar','Due',fmtDate(a.dueDate)],['fa-play-circle','Start',fmtDate(a.startDate)]].map(([ic,lb,val])=>(
                                            <div key={lb} style={{fontSize:12,color:'#64748b',display:'flex',alignItems:'center',gap:5}}>
                                                <i className={`fas ${ic}`} style={{color:c,fontSize:10,width:12}} />{val}
                                            </div>
                                        ))}
                                    </div>
                                    {sub?.status==='graded' && (
                                        <div style={{background:'#fef3c7',border:'1.5px solid #fde68a',borderRadius:10,padding:'8px 12px',marginBottom:8}}>
                                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                                                <span style={{fontSize:11,fontWeight:700,color:'#92400e'}}><i className="fas fa-star" style={{marginRight:4}} />Score</span>
                                                <span style={{fontSize:18,fontWeight:800,color:'#d97706'}}>{sub.grade}/{a.maxMarks}</span>
                                            </div>
                                            {sub.feedback && <div style={{fontSize:11,color:'#92400e',marginTop:4,fontStyle:'italic'}}><i className="fas fa-comment-alt" style={{marginRight:4}} />{sub.feedback}</div>}
                                        </div>
                                    )}
                                    {status !== 'graded' && dl !== null && (
                                        <div style={{marginBottom:8,fontSize:12,fontWeight:700,color:dl<0?'#dc2626':dl<=2?'#f59e0b':'#10b981'}}>
                                            <i className="fas fa-clock" style={{marginRight:5}} />
                                            {dl<0?`${Math.abs(dl)} days overdue`:dl===0?'Due today!':dl===1?'1 day left':`${dl} days left`}
                                        </div>
                                    )}
                                    {(status==='pending'||status==='submitted'||status==='overdue'||status==='late') && (
                                        <button onClick={()=>openSubmit(a)}
                                            style={{width:'100%',padding:'9px',background:`linear-gradient(135deg,${PURPLE},#8b5cf6)`,color:'#fff',border:'none',borderRadius:10,fontSize:13,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:7}}>
                                            <i className={`fas ${sub?'fa-pen':'fa-paper-plane'}`} />{sub?'Edit & Resubmit':'Start & Submit'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* History */}
            {tab === 'history' && (
                <div style={{display:'flex',flexDirection:'column',gap:12}}>
                    {done.length === 0 && (
                        <div style={{textAlign:'center',padding:'48px',color:'#94a3b8',background:'#f8fafc',borderRadius:14,border:'2px dashed #e2e8f0'}}>
                            <i className="fas fa-history" style={{fontSize:32,display:'block',marginBottom:10}} />
                            <div style={{fontSize:14,fontWeight:700,color:'#374151'}}>No submissions yet</div>
                        </div>
                    )}
                    {done.map(a => {
                        const sub = mySubmissions.find(s=>s.assignmentId===a.id);
                        const c   = colorOf(a.subject||'x');
                        return (
                            <div key={a.id} className="am-card-hover" style={{background:'#fff',borderRadius:14,border:'1px solid #e2e8f0',overflow:'hidden',boxShadow:'0 2px 8px rgba(0,0,0,0.04)'}}>
                                <div style={{height:4,background:c}} />
                                <div style={{padding:'14px 18px'}}>
                                    <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12,marginBottom:8}}>
                                        <div>
                                            <div style={{fontWeight:800,fontSize:14,color:'#1f2937',marginBottom:4}}>{a.title}</div>
                                            <div style={{display:'flex',gap:7,flexWrap:'wrap',alignItems:'center'}}>
                                                <SubjectChip subject={a.subject} />
                                                <span style={{fontSize:12,color:'#64748b'}}><i className="fas fa-clock" style={{marginRight:4}} />Submitted: {fmtDT(sub?.submittedAt)}</span>
                                            </div>
                                        </div>
                                        <div style={{textAlign:'right',flexShrink:0}}>
                                            <Badge status={sub?.status} />
                                            {sub?.grade != null && <div style={{fontSize:18,fontWeight:800,color:'#d97706',marginTop:4}}>{sub.grade}<span style={{fontSize:12,color:'#92400e'}}>/{a.maxMarks}</span></div>}
                                        </div>
                                    </div>
                                    <div style={{background:'#f8fafc',borderRadius:8,padding:'8px 12px',fontSize:12,color:'#64748b',border:'1px solid #e2e8f0',whiteSpace:'pre-wrap',maxHeight:60,overflow:'hidden',lineHeight:1.5}}>
                                        {sub?.answerText || <span style={{fontStyle:'italic'}}>No answer text</span>}
                                    </div>
                                    {sub?.feedback && (
                                        <div style={{marginTop:8,background:'#fef3c7',border:'1px solid #fde68a',borderRadius:8,padding:'7px 12px',fontSize:12,color:'#92400e'}}>
                                            <strong>Feedback: </strong>{sub.feedback}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Submit panel overlay */}
            {panel && (
                <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
                    <div style={{background:'#fff',borderRadius:20,maxWidth:680,width:'100%',maxHeight:'90vh',overflow:'hidden',display:'flex',flexDirection:'column',boxShadow:'0 24px 64px rgba(99,102,241,0.2)'}}>
                        <div style={{background:`linear-gradient(135deg,${PURPLE},#8b5cf6)`,padding:'18px 24px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                            <div>
                                <div style={{color:'#fff',fontWeight:800,fontSize:16}}>{panel.title}</div>
                                <div style={{color:'rgba(255,255,255,0.7)',fontSize:12,marginTop:2}}>{panel.subject} · Max {panel.maxMarks} marks · Due {fmtDate(panel.dueDate)}</div>
                            </div>
                            <button onClick={()=>setPanel(null)} style={{background:'rgba(255,255,255,0.2)',border:'none',color:'#fff',borderRadius:'50%',width:32,height:32,fontSize:18,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
                        </div>
                        <div style={{padding:24,overflowY:'auto',flex:1}}>
                            {toast && <div style={{background:'#fee2e2',border:'1.5px solid #fca5a5',borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:13,color:'#dc2626',fontWeight:700}}><i className="fas fa-exclamation-circle" style={{marginRight:7}} />{toast}</div>}
                            <div style={{background:'#f8fafc',borderRadius:12,padding:'14px',marginBottom:16,border:'1px solid #e2e8f0'}}>
                                <p style={{fontSize:13,color:'#374151',marginBottom:8}}>{panel.description}</p>
                                <div style={{background:`${PURPLE}08`,border:`1px solid ${PURPLE}20`,borderRadius:8,padding:'10px 12px',fontSize:13,color:'#374151'}}>
                                    <strong style={{color:PURPLE}}>Instructions: </strong>{panel.instructions}
                                </div>
                            </div>
                            <div style={{fontSize:12,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:7}}>
                                <i className="fas fa-keyboard" style={{marginRight:6,color:PURPLE}} />Your Answer
                            </div>
                            <textarea value={answer} onChange={e=>{setAnswer(e.target.value);setToast('');}} placeholder="Type your answer here…" rows={10}
                                style={{width:'100%',padding:'12px',border:'1.5px solid #e2e8f0',borderRadius:12,fontSize:13,resize:'vertical',outline:'none',lineHeight:1.6}} />
                            <div style={{textAlign:'right',fontSize:11,color:'#94a3b8',marginTop:4}}>{answer.length} characters</div>
                        </div>
                        <div style={{padding:'14px 24px',borderTop:'1px solid #e2e8f0',display:'flex',gap:10,justifyContent:'flex-end',background:'#f8fafc'}}>
                            <GhostBtn onClick={()=>setPanel(null)}>Cancel</GhostBtn>
                            <PrimaryBtn onClick={submit} style={{minWidth:160}}>
                                <i className="fas fa-paper-plane" /> Submit Assignment
                            </PrimaryBtn>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const PATH_TO_TAB = {
    '/assignment':             'dashboard',
    '/assignment/dashboard':   'dashboard',
    '/assignment/create':      'create',
    '/assignment/submissions': 'grading',
};
const TAB_TO_PATH = {
    dashboard: '/assignment/dashboard',
    create:    '/assignment/create',
    grading:   '/assignment/submissions',
};

// ─── Main Component ────────────────────────────────────────────────────────────
function AssignmentHome({ history, location }) {
    const client = useApolloClient();

    // Role-based access control. Declared FIRST so the data-fetching effects below can
    // bail on it — they're defined above where it would otherwise be declared, and a
    // guard referenced before its `const` would be a temporal-dead-zone hazard.
    //
    // Note: the `role` state further down is NOT an auth role — it's a hardcoded
    // teacher/student UI mode. This guard is the real grant check.
    const guard = useModuleGuard(ASSIGNMENTS_MODULE);

    const [students,    setStudents]    = useState(INIT_STUDENTS);
    const [groups,      setGroups]      = useState(INIT_GROUPS);
    const [assignments, setAssignments] = useState([]);
    const [submissions, setSubmissions] = useState([]);

    // ── Filter state ────────────────────────────────────────────────────────────
    const [academicYears,    setAcademicYears]    = useState([]);
    const [selAcademicYear,  setSelAcademicYear]  = useState(null);
    const [branches,    setBranches]    = useState([]);
    const [classes,     setClasses]     = useState([]);
    const [selBranches, setSelBranches] = useState([]);   // from the header's branch scope, not a dropdown
    const [selClasses,  setSelClasses]  = useState([]);   // multi-select
    const [selStreams,  setSelStreams]  = useState([]);   // multi-select (Intermediate only)
    const [selSections, setSelSections] = useState([]);   // multi-select
    const [allStreams,  setAllStreams]  = useState([]);   // { id, stream }
    const [loadingBr,   setLoadingBr]   = useState(true);
    const [loadingAsgn, setLoadingAsgn] = useState(false);
    // Prevents the reactive fetch useEffect from firing during initial data load
    const [filterReady, setFilterReady] = useState(false);
    // Bumped whenever the header's branch selection changes, to re-run the loader.
    const [branchScopeTick, setBranchScopeTick] = useState(0);

    const toUnixStr = (dateStr) => dateStr ? String(Math.round(new Date(dateStr).getTime() / 1000)) : '';
    const isoOffset = (days) => new Date(Date.now() + days * 864e5).toISOString().split('T')[0];
    const [fromDate, setFromDate] = useState(() => isoOffset(-180));
    const [toDate,   setToDate]   = useState(() => isoOffset(180));

    // ── Load all filter data in parallel, then do exactly one initial fetch ──────
    useEffect(() => {
        // is_view === 0 → fetch nothing. Every read here is an imperative client.query()
        // (there are no graphql HOCs to put a `skip` on), so bailing out of the effects
        // is what keeps the module's data off the wire.
        if (!guard.canView()) return;

        const institutionId = parseInt(Cookies.get('institutionid'));

        Promise.all([
            client.query({ query: GET_ACADEMIC_YEARS, variables: { id: 1 },                          fetchPolicy: 'network-only' }),
            client.query({ query: GET_BRANCHES,        variables: { institution_id: institutionId },  fetchPolicy: 'network-only' }),
            client.query({ query: GET_CLASSES,         variables: { id: 1 },                          fetchPolicy: 'network-only' }),
            client.query({ query: GET_STREAMS,                                                         fetchPolicy: 'network-only' }),
        ]).then(([ayRes, brRes, clRes, stRes]) => {
            // ── academic years ──────────────────────────────────────────────────
            const ayData = ayRes.data?.getAcademicYears || [];
            const now = new Date();
            const yr  = now.getFullYear();
            const mo  = now.getMonth() + 1;
            const ayStart = mo >= 4 ? yr : yr - 1;
            const currentAY = `${ayStart}-${String(ayStart + 1).slice(-2)}`;
            const currentAYObj = ayData.find(y => y.academic_year === currentAY) || ayData[0];
            const newAyId = currentAYObj?.id || null;

            // ── branches / sections ─────────────────────────────────────────────
            // The header's branch selection (NavbarOne) IS this list's branch filter,
            // and it never widens past the branches the user is authorized for.
            const brData = scopeBranches(brRes.data?.getBranches || []);
            const newBranches = brData.map(b => b.id);
            let allSecs = brData.flatMap(b => b.sections || []);
            allSecs = filterByAuthorizedIds(allSecs, getAuthorizedSectionIds(), 'id');

            // ── classes ─────────────────────────────────────────────────────────
            // Only the classes present in the branches' sections and authorized for the user — so the Class
            // dropdown's "Select All" is checked by default (matches its options).
            const clData    = clRes.data?.getExamClasses || [];
            const authorizedClData = filterByAuthorizedIds(clData, getAuthorizedClassIds(), 'id');
            const classIdsInBranches = new Set(allSecs.map(s => Number(s.class_id)));
            const newClasses = authorizedClData.filter(c => classIdsInBranches.has(Number(c.id))).map(c => c.id);

            // ── streams (Intermediate only) ─────────────────────────────────────
            const stData = stRes.data?.getStreams || [];
            // Select streams present across sections of the default classes; fallback to stData
            let newStreams = streamIdsFor(allSecs, newClasses);
            if (!newStreams.length && stData.length) {
                newStreams = stData.map(st => Number(st.id));
            }
            // Sections, already narrowed by the (all-selected) streams.
            const newSections = sectionIdsFor(allSecs, newClasses, newStreams);

            // ── compute dates from academic year (without relying on state) ─────
            let from_date, to_date;
            if (newAyId && currentAYObj?.academic_year) {
                const startYr = parseInt(currentAYObj.academic_year.split('-')[0]);
                if (!isNaN(startYr)) {
                    from_date = toUnixStr(`${startYr}-04-01`);
                    to_date   = toUnixStr(`${startYr + 1}-03-31`);
                }
            }
            if (!from_date) {
                from_date = toUnixStr(isoOffset(-180));
                to_date   = toUnixStr(isoOffset(180));
            }

            // ── single initial fetch ────────────────────────────────────────────
            setLoadingAsgn(true);
            client.query({
                query: GET_STUDENT_ASSIGNMENTS,
                variables: {
                    institute_id:             institutionId,
                    branch_id:                newBranches.join(','),
                    class_id:                 newClasses.join(','),
                    section_ids:              newSections.join(','),
                    dairy_assignment_type_id: 2,
                    from_date,
                    to_date,
                },
                fetchPolicy: 'network-only',
            }).then(res => {
                const mapped = (res.data?.getStudentAssignmentDetails || []).map(mapApiAssignment);
                setAssignments(mapped);
                setLoadingAsgn(false);
                fetchAllSubmissions(mapped);
            }).catch(() => setLoadingAsgn(false));

            // ── update UI state (filterReady=false guards against extra fetches) ─
            setAcademicYears(ayData);
            setBranches(brData);
            setClasses(clData);
            setAllStreams(stData);
            setSelAcademicYear(newAyId);
            setSelBranches(newBranches);
            setSelClasses(newClasses);
            setSelStreams(newStreams);
            setSelSections(newSections);
            // Reflect the academic year's date range in the date pickers so they match
            if (newAyId && currentAYObj?.academic_year) {
                const startYr = parseInt(currentAYObj.academic_year.split('-')[0]);
                if (!isNaN(startYr)) {
                    setFromDate(`${startYr}-04-01`);
                    setToDate(`${startYr + 1}-03-31`);
                }
            }
            setLoadingBr(false);
            setFilterReady(true);  // unlocks the reactive useEffect for user changes
        }).catch(() => setLoadingBr(false));
    }, [branchScopeTick]);

    // Branch comes from the header (NavbarOne). Bumping the tick re-runs the loader
    // above, which re-scopes the branch list and re-derives class/stream/section.
    useEffect(() => {
        const off = onBranchScopeChange(() => {
            setFilterReady(false);   // don't let the reactive fetch fire mid-reload
            setBranchScopeTick(t => t + 1);
        });
        return off;
    }, []);

    // ── Helpers for cascading multi-select ──────────────────────────────────────
    // Distinct stream_ids of stream-class sections within the given classes.
    const streamIdsFor = (sectionList, classIds) => {
        const ids = sectionList
            .filter(s => (!classIds.length || classIds.map(Number).includes(Number(s.class_id)))
                && isStreamClass(s.class_id, classes) && s.stream_id != null)
            .map(s => Number(s.stream_id));
        return [...new Set(ids)];
    };

    // Section ids within the given classes, narrowed by stream for stream-classes.
    // Non-stream classes ignore the stream filter; stream-classes keep only
    // sections whose stream_id is in streamIds.
    const sectionIdsFor = (sectionList, classIds, streamIds) =>
        sectionList
            .filter(s => !classIds.length || classIds.map(Number).includes(Number(s.class_id)))
            .filter(s => !isStreamClass(s.class_id, classes) || s.stream_id == null || streamIds.map(Number).includes(Number(s.stream_id)))
            .map(s => s.id);

    // Flat sections list for the selected branches, filtered by user authorization.
    const sectionsForBranches = (branchIds) =>
        filterByAuthorizedIds(
            branches.filter(b => branchIds.includes(b.id)).flatMap(b => b.sections || []),
            getAuthorizedSectionIds(),
            'id'
        );

    const allClassesForBranches = (branchIds) => {
        const ids = new Set(
            branches.filter(b => branchIds.includes(b.id))
                .flatMap(b => b.sections || [])
                .map(s => Number(s.class_id))
        );
        const authorizedClasses = filterByAuthorizedIds(classes, getAuthorizedClassIds(), 'id');
        return authorizedClasses.filter(c => ids.has(Number(c.id))).map(c => c.id);
    };

    // ── Derived options for the filter bar ──────────────────────────────────────
    // No branchOpts: Branch is not a dropdown here any more. selBranches is set
    // once from the header's scope and is read-only from the UI's point of view.
    const classOpts = (() => {
        const ids = new Set(
            branches.filter(b => selBranches.includes(b.id))
                .flatMap(b => b.sections || [])
                .map(s => Number(s.class_id))
        );
        const authorizedClasses = filterByAuthorizedIds(classes, getAuthorizedClassIds(), 'id');
        return authorizedClasses
            .filter(c => ids.size === 0 || ids.has(Number(c.id)))
            .map(c => ({ value: c.id, label: c.class }));
    })();

    const sectionOpts = filterByAuthorizedIds(
        branches
            .filter(b => selBranches.includes(b.id))
            .flatMap(b => b.sections || []),
        getAuthorizedSectionIds(),
        'id'
    )
        .filter(s => !selClasses.length || selClasses.map(Number).includes(Number(s.class_id)))
        // Drop stream-class sections whose stream is not currently selected.
        .filter(s => !isStreamClass(s.class_id, classes) || s.stream_id == null || selStreams.map(Number).includes(Number(s.stream_id)))
        .map(s => ({ value: s.id, label: s.section_name }));

    // ── Stream options (only shown when stream-classes are in the selection) ─────
    const streamOpts = (() => {
        const currentClassIds = selClasses.length > 0 ? selClasses : allClassesForBranches(selBranches);
        const hasStreamClass = currentClassIds.some(cid => isStreamClass(cid, classes));
        if (!hasStreamClass) return [];

        const ids = streamIdsFor(sectionsForBranches(selBranches), selClasses);
        if (ids.length > 0) {
            return ids.map(id => {
                const found = allStreams.find(st => Number(st.id) === Number(id));
                return { value: id, label: found ? found.stream : `Stream ${id}` };
            });
        }
        if (allStreams && allStreams.length > 0) {
            return allStreams.map(st => ({ value: Number(st.id), label: st.stream }));
        }
        return [];
    })();
    const showStream = streamOpts.length > 0;

    useEffect(() => {
        if (filterReady && streamOpts.length > 0 && selStreams.length === 0) {
            setSelStreams(streamOpts.map(o => o.value));
        }
    }, [filterReady, streamOpts.map(o => o.value).join(',')]);

    // ── Derive date range string from an academic year object ────────────────────
    const ayToDateRange = (ay) => {
        if (!ay?.academic_year) return null;
        const startYr = parseInt(ay.academic_year.split('-')[0]);
        if (isNaN(startYr)) return null;
        return { from: `${startYr}-04-01`, to: `${startYr + 1}-03-31` };
    };

    // ── Fetch assignments on user-initiated filter changes only ───────────────────
    // filterReady is intentionally NOT in deps — it only guards the initial load.
    useEffect(() => {
        if (!filterReady) return;
        if (!guard.canView()) return;   // is_view === 0 → don't fetch the assignments

        const institutionId = parseInt(Cookies.get('institutionid'));
        setLoadingAsgn(true);
        client.query({
            query: GET_STUDENT_ASSIGNMENTS,
            variables: {
                institute_id:             institutionId,
                branch_id:                selBranches.join(','),
                class_id:                 selClasses.join(','),
                section_ids:              selSections.join(','),
                dairy_assignment_type_id: 2,
                from_date:                toUnixStr(fromDate),
                to_date:                  toUnixStr(toDate),
            },
            fetchPolicy: 'network-only',
        }).then(res => {
            const raw = res.data?.getStudentAssignmentDetails || [];
            setAssignments(raw.map(mapApiAssignment));
            setLoadingAsgn(false);
        }).catch(() => setLoadingAsgn(false));
    }, [selBranches, selClasses, selSections, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch submissions for every assignment in the list in parallel,
    // then merge them into the shared submissions state.
    const fetchAllSubmissions = (assignmentsList) => {
        if (!assignmentsList || !assignmentsList.length) { setSubmissions([]); return; }
        Promise.all(
            assignmentsList.map(a =>
                client.query({
                    query: GET_ASSIGNMENT_SUBMISSIONS,
                    variables: { assignment_id: Number(a.id) },
                    fetchPolicy: 'network-only',
                }).then(r => r.data?.getAssignmentSubmissions || [])
                  .catch(() => [])
            )
        ).then(results => {
            const flat = results.flat();
            // Deduplicate by id in case the backend returns overlapping records
            const unique = [...new Map(flat.map(s => [s.id, s])).values()];
            setSubmissions(unique);
        });
    };

    const fetchAssignments = () => {
        const institutionId = parseInt(Cookies.get('institutionid'));
        setLoadingAsgn(true);
        client.query({
            query: GET_STUDENT_ASSIGNMENTS,
            variables: {
                institute_id:             institutionId,
                branch_id:                selBranches.join(','),
                class_id:                 selClasses.join(','),
                section_ids:              selSections.join(','),
                dairy_assignment_type_id: 2,
                from_date:                toUnixStr(fromDate),
                to_date:                  toUnixStr(toDate),
            },
            fetchPolicy: 'network-only',
        }).then(res => {
            const mapped = (res.data?.getStudentAssignmentDetails || []).map(mapApiAssignment);
            setAssignments(mapped);
            setLoadingAsgn(false);
            fetchAllSubmissions(mapped);
        }).catch(() => setLoadingAsgn(false));
    };

    const [role,        setRole]        = useState('teacher');
    const [teacherTab,  setTeacherTab]  = useState(() => PATH_TO_TAB[location?.pathname] || 'dashboard');
    const [studentId,   setStudentId]   = useState(1);
    const [gradeTarget, setGradeTarget] = useState(null);
    const [editTarget,  setEditTarget]  = useState(null);

    // Keep tab in sync if the user navigates via browser back/forward
    useEffect(() => {
        const tab = PATH_TO_TAB[location?.pathname] || 'dashboard';
        setTeacherTab(tab);
    }, [location?.pathname]);

    // ── Data helpers ────────────────────────────────────────────────────────────
    const saveAssignment  = d => { fetchAssignments(); };

    // Last gate before DELETE_ASSIGNMENT leaves the client. TeacherDashboard's trash
    // icon and its confirm dialog both stay clickable; this is what stops the delete.
    const deleteAssignment = (id) => {
        if (!guard.requireDelete()) return;

        client.mutate({
            mutation: DELETE_ASSIGNMENT,
            variables: { assignment_id: id },
        }).then(() => {
            setAssignments(prev => prev.filter(a => a.id !== id));
        }).catch(() => {});
    };

    const gradeSubmission  = d => setSubmissions(prev => prev.map(s=>s.id===d.id?d:s));
    const addSubmission    = d => setSubmissions(prev => [...prev, d]);
    const updateSubmission = d => setSubmissions(prev => prev.map(s=>s.id===d.id?d:s));

    const addGroup    = d => setGroups(prev => [...prev, d]);
    const updateGroup = d => setGroups(prev => prev.map(g=>g.id===d.id?d:g));
    const deleteGroup = id => setGroups(prev => prev.filter(g=>g.id!==id));

    const addStudent    = d => setStudents(prev => [...prev, d]);
    const updateStudent = d => setStudents(prev => prev.map(s=>s.id===d.id?d:s));
    const deleteStudent = id => setStudents(prev => prev.filter(s=>s.id!==id));

    // The "create" tab IS the create/edit form, so routing to it is an is_edit action.
    // Checked BEFORE history.push, otherwise react-router would already have moved the
    // user into the form. "dashboard" and "grading" are reads and pass through.
    // (AssignmentForm re-checks on save too — /assignment/create can be typed directly,
    // which mounts the form without ever going through here.)
    const goToTab = (tab) => {
        if (tab === 'create' && !guard.requireEdit()) return;

        setTeacherTab(tab);
        history.push(TAB_TO_PATH[tab]);
        // Refresh submission counts whenever returning to dashboard so graded records are current
        if (tab === 'dashboard' && assignments.length > 0) {
            fetchAllSubmissions(assignments);
        }
    };

    const handleGrade = assignment => { setGradeTarget(assignment); goToTab('grading'); };

    // Row "edit" pencil — gated before the form is opened. goToTab('create') would catch
    // it anyway, but checking here avoids setting editTarget on a denied click.
    const handleEdit  = assignment => {
        if (!guard.requireEdit()) return;
        setEditTarget(assignment);
        goToTab('create');
    };

    const pendingReviews = submissions.filter(s=>s.status==='submitted').length;
    const activeStudent  = students.find(s=>s.id===studentId) || students[0];

    const teacherTabs = [
        { key:'dashboard', label:'Dashboard',          icon:'fal fa-tachometer-alt' },
        { key:'create',    label:'Create Assignment',  icon:'fal fa-plus-circle'    },
        { key:'grading',   label:'Submissions Review', icon:'fal fa-clipboard-check'},
    ];

    // is_view === 0 → no assignment data is rendered. This sits AFTER every hook (the
    // last useEffect is above) so the hook order stays stable — an early return placed
    // among the hooks would break the rules of hooks. The useEffects also bail on their
    // own (see the guard checks in them), so nothing is fetched either.
    if (!guard.canView()) {
        return (
            <div style={{background:'linear-gradient(135deg,#f0f4ff 0%,#faf5ff 100%)',minHeight:'100vh',padding:'24px 28px'}}>
                <AccessDeniedCard />
            </div>
        );
    }

    return (
        <div style={{background:'linear-gradient(135deg,#f0f4ff 0%,#faf5ff 100%)',minHeight:'100vh',padding:'24px 28px'}}>
            <ShimmerStyles />
            <Container fluid className="px-0">

                {/* Main card */}
                <div style={{background:'#fff',borderRadius:20,boxShadow:'0 4px 24px rgba(0,0,0,0.06)',border:'1px solid #e2e8f0'}}>

                    {/* Tab bar + role switcher (same row) */}
                    <div style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0', padding: '12px 24px 0', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', borderRadius: '20px 20px 0 0' }}>

                        {/* Left: teacher tabs or student spacer */}
                        <ul className="nav nav-tabs" style={{ borderBottom: 'none', gap: 8, flex: 1 }}>
                            {role === 'teacher' && teacherTabs.map(({ key, label, icon }) => {
                                const isActive = teacherTab === key;
                                return (
                                    <li className="nav-item" key={key}>
                                        <a
                                            className={`nav-link ${isActive ? 'active' : ''}`}
                                            href="#"
                                            onClick={e => {
                                                e.preventDefault();
                                                // Opening Create from the nav should always be a fresh form
                                                if (key === 'create') setEditTarget(null);
                                                if (key === 'grading') setGradeTarget(null);
                                                goToTab(key);
                                            }}
                                            style={{
                                                cursor: 'pointer',
                                                fontWeight: 700,
                                                fontSize: 14,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                padding: '12px 20px',
                                                color: isActive ? '#6366f1' : '#4b5563',
                                                background: isActive ? '#fff' : 'transparent',
                                                borderColor: isActive ? '#e2e8f0 #e2e8f0 #fff' : 'transparent',
                                                borderWidth: '1.5px',
                                                borderBottomWidth: isActive ? '2px' : '1.5px',
                                                borderRadius: '10px 10px 0 0',
                                                transition: 'all 0.15s ease',
                                                marginBottom: '-1.5px',
                                                textDecoration: 'none',
                                            }}
                                        >
                                            <i className={icon} />
                                            {label}
                                            {key === 'grading' && pendingReviews > 0 && (
                                                <span style={{ background: '#ef4444', color: '#fff', borderRadius: 20, padding: '1px 7px', fontSize: 10, fontWeight: 800 }}>
                                                    {pendingReviews}
                                                </span>
                                            )}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>

                        {/* Right: student dropdown + role switcher */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 10, flexShrink: 0 }}>
                            {role === 'student' && (
                                <select value={studentId} onChange={e => setStudentId(parseInt(e.target.value))}
                                    style={{ padding: '7px 12px', border: '1.5px solid #e2e8f0', borderRadius: 9, fontSize: 13, color: '#374151', background: '#fff', outline: 'none', fontWeight: 600 }}>
                                    {students.map(s => <option key={s.id} value={s.id}>{s.name} ({s.roll})</option>)}
                                </select>
                            )}
                            {/* Role switcher (Teacher / Student) hidden — default to Teacher view showing all tabs
                            <div style={{ display: 'flex', background: '#fff', borderRadius: 12, padding: 4, gap: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }}>
                                {[['teacher', 'Teacher', 'fa-chalkboard-teacher', PURPLE], ['student', 'Student', 'fa-user-graduate', '#10b981']].map(([key, label, icon, color]) => (
                                    <button key={key} onClick={() => setRole(key)}
                                        style={{ padding: '8px', borderRadius: 9, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, background: role === key ? color : 'transparent', color: role === key ? '#fff' : '#64748b', transition: 'all 0.2s', boxShadow: role === key ? `0 3px 8px ${color}40` : 'none' }}>
                                        <i className={`fas ${icon}`} />{label}
                                    </button>
                                ))}
                            </div>
                            */}
                        </div>
                    </div>

                    {/* Student context bar */}
                    {role === 'student' && activeStudent && (
                        <div style={{ background: '#f0fdf4', borderBottom: '1px solid #bbf7d0', padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 26, height: 26, background: '#16a34a20', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#16a34a', fontSize: 11 }}>{activeStudent.name.charAt(0)}</div>
                            <span style={{ fontWeight: 700, color: '#15803d', fontSize: 13 }}>Viewing as: {activeStudent.name} ({activeStudent.roll})</span>
                        </div>
                    )}

                    {/* Filter bar — dashboard tab only */}
                    {role === 'teacher' && teacherTab === 'dashboard' && (
                        <div style={{background:'#faf8ff',border:'1.5px solid #ede9fe',borderRadius:14,padding:'12px 18px 16px',margin:'16px 28px 0'}}>
                            {/* Title row */}
                            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                                <div style={{display:'flex',alignItems:'center',gap:6}}>
                                    <div style={{width:28,height:28,background:`${PURPLE}18`,borderRadius:7,display:'flex',alignItems:'center',justifyContent:'center'}}>
                                        <i className="fas fa-sliders-h" style={{color:PURPLE,fontSize:12}} />
                                    </div>
                                    <span style={{fontSize:12,fontWeight:800,color:PURPLE,letterSpacing:'0.3px'}}>Filters</span>
                                </div>
                                {loadingAsgn && (
                                    <div style={{display:'flex',alignItems:'center',gap:6,background:`${PURPLE}10`,borderRadius:8,padding:'4px 12px'}}>
                                        <i className="fas fa-spinner fa-spin" style={{color:PURPLE,fontSize:12}} />
                                        <span style={{fontSize:12,color:PURPLE,fontWeight:600}}>Loading…</span>
                                    </div>
                                )}
                            </div>

                            {/* Filter controls row */}
                            <div style={{display:'flex',alignItems:'flex-end',gap:10,flexWrap:'wrap'}}>
                                {/* Academic Year */}
                                <div style={{display:'flex',flexDirection:'column',gap:3,minWidth:160,maxWidth:200,flex:'1 1 160px'}}>
                                    <label style={{fontSize:10,fontWeight:700,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.5px'}}>Academic Year</label>
                                    <select
                                        value={selAcademicYear || ''}
                                        onChange={e => {
                                            const ayId = e.target.value ? parseInt(e.target.value) : null;
                                            setSelAcademicYear(ayId);
                                            if (ayId) {
                                                const ay = academicYears.find(y => y.id === ayId);
                                                const range = ayToDateRange(ay);
                                                if (range) { setFromDate(range.from); setToDate(range.to); }
                                            }
                                        }}
                                        style={{borderRadius:10,border:'1.5px solid #e5e7eb',padding:'9px 14px',fontSize:14,color:selAcademicYear?'#1f2937':'#9ca3af',background:'#f9fafb',outline:'none',width:'100%',cursor:'pointer',userSelect:'none'}}>
                                        <option value="">All Years</option>
                                        {academicYears.map(y => <option key={y.id} value={y.id}>{y.academic_year}</option>)}
                                    </select>
                                </div>

                                <div style={{width:1,height:36,background:'#e0d9ff',flexShrink:0}} />

                                {/* No Branch filter — the header's branch selection scopes this list. */}

                                {/* Class — multi select */}
                                <div style={{display:'flex',flexDirection:'column',gap:3,minWidth:160,maxWidth:200,flex:'1 1 160px'}}>
                                    <label style={{fontSize:10,fontWeight:700,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.5px'}}>Class</label>
                                    <CheckboxDropdown
                                        options={classOpts}
                                        value={selClasses}
                                        onChange={val => {
                                            const branchSecs  = sectionsForBranches(selBranches);
                                            const newStreams  = streamIdsFor(branchSecs, val);
                                            const newSections = sectionIdsFor(branchSecs, val, newStreams);
                                            setSelClasses(val);
                                            setSelStreams(newStreams);
                                            setSelSections(newSections);
                                        }}
                                        placeholder="All Classes"
                                        disabled={loadingBr}
                                    />
                                </div>

                                <div style={{width:1,height:36,background:'#e0d9ff',flexShrink:0}} />

                                {/* Stream — multi select (Intermediate classes only) */}
                                {showStream && (
                                    <>
                                        <div style={{display:'flex',flexDirection:'column',gap:3,minWidth:160,maxWidth:200,flex:'1 1 160px'}}>
                                            <label style={{fontSize:10,fontWeight:700,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.5px'}}>Stream</label>
                                            <CheckboxDropdown
                                                options={streamOpts}
                                                value={selStreams}
                                                onChange={val => {
                                                    const branchSecs  = sectionsForBranches(selBranches);
                                                    const newSections = sectionIdsFor(branchSecs, selClasses, val);
                                                    setSelStreams(val);
                                                    setSelSections(newSections);
                                                }}
                                                placeholder="All Streams"
                                                disabled={loadingBr}
                                            />
                                        </div>

                                        <div style={{width:1,height:36,background:'#e0d9ff',flexShrink:0}} />
                                    </>
                                )}

                                {/* Section — multi select */}
                                <div style={{display:'flex',flexDirection:'column',gap:3,minWidth:160,maxWidth:220,flex:'1 1 160px'}}>
                                    <label style={{fontSize:10,fontWeight:700,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.5px'}}>Section</label>
                                    <CheckboxDropdown
                                        options={sectionOpts}
                                        value={selSections}
                                        onChange={setSelSections}
                                        placeholder="All Sections"
                                        disabled={loadingBr}
                                    />
                                </div>

                                <div style={{width:1,height:36,background:'#e0d9ff',flexShrink:0}} />

                                {/* Date Range */}
                                <div style={{display:'flex',flexDirection:'column',gap:3,minWidth:230,flex:'0 0 230px'}}>
                                    <label style={{fontSize:10,fontWeight:700,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.5px'}}>Date Range</label>
                                    <style>{`.asgn-daterange,.asgn-daterange .customDaterange,.asgn-daterange #DateTimeRangeContainerNoMobileMode{width:100%!important}`}</style>
                                    <div className="asgn-daterange">
                                        <DateBlock
                                            applyCallback={(s, e) => { setFromDate(s.format('YYYY-MM-DD')); setToDate(e.format('YYYY-MM-DD')); }}
                                            initialStart={fromDate}
                                            initialEnd={toDate}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Content */}
                    <div style={{padding:28}}>
                        {role==='teacher' && teacherTab==='dashboard'   && <TeacherDashboard assignments={assignments} submissions={submissions} groups={groups} students={students} onTabChange={goToTab} onDeleteAssignment={deleteAssignment} onGrade={handleGrade} onEdit={handleEdit} loading={loadingAsgn} />}
                        {role==='teacher' && teacherTab==='create'       && <AssignmentForm groups={groups} editing={editTarget} onSave={()=>{
                            // Start refreshing the assignments list in background
                            saveAssignment();
                            setEditTarget(null);
                            // After publishing/saving, return to the Dashboard
                            goToTab('dashboard');
                        }} onCancel={()=>{setEditTarget(null);goToTab('dashboard');}} guard={guard} />}
                        {role==='teacher' && teacherTab==='grading'      && <GradingInbox assignments={assignments} initialAssignment={gradeTarget} loadingAssignments={loadingAsgn} fromDate={fromDate} toDate={toDate} setFromDate={setFromDate} setToDate={setToDate} guard={guard} />}
                        {/* {role==='teacher' && teacherTab==='groups'       && <GroupsManager groups={groups} students={students} onAddGroup={addGroup} onUpdateGroup={updateGroup} onDeleteGroup={deleteGroup} />} */}
                        {role==='student' && activeStudent               && <StudentView student={activeStudent} assignments={assignments} groups={groups} submissions={submissions} onAddSubmission={addSubmission} onUpdateSubmission={updateSubmission} />}
                    </div>
                </div>

                {/* Shown instead of creating / editing / grading / deleting when the
                    grant is missing. One modal serves every intercepted action, in this
                    component and in the AssignmentForm / GradingInbox children (they
                    share this same guard instance via the `guard` prop). */}
                <AccessDeniedModal
                    show={guard.accessDeniedShow}
                    onHide={guard.hideAccessDenied}
                />

            </Container>
        </div>
    );
}

export default withRouter(AssignmentHome);
